import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useGameState } from '../../components/WorkingChessLogic/GameStateContext';
import useGameLogic from '../../components/WorkingChessLogic/UseGameState';
import { useUnifiedStockfishAnalysis } from '../../hooks/useUnifiedStockfishAnalysis';
import BestLineComponent from './BestLineComponent.jsx';
import { Chess } from 'chess.js';
import constants from '../../util/constants';
import { 
  getNodeAtPathInfinite,
  getPathDepth,
  parseVariationPath,
  getParentPath,
  isInVariation as isInVariationPath,
  getMoveSequenceToPath,
  getNextMovePath,
  getPreviousMovePath,
  calculateCumulativeMoveIndex
} from '../../util/VariationPathUtils';
import UnifiedMobileCommentInput from './UnifiedMobileCommentInput.jsx';
import { cleanProfanity } from '../../util/filterProfanity';

const CHESSBOARD = constants.CHESSBOARD;

// Mobile Comment Input Component - Now using UnifiedMobileCommentInput
const MobileCommentInput = ({ 
  onSubmit, 
  currentMoveNotation, 
  currentMove, // Add currentMove prop to check if we're at start position
  disabled = false, 
  submitting = false,
  rateLimitError = null
}) => {
  const handleSubmit = (commentData) => {
    if (onSubmit) {
      onSubmit(commentData);
    } else {
      // Fallback for backward compatibility
      // console.log('Comment submitted:', commentData);
    }
  };

  return (
    <UnifiedMobileCommentInput
      onSubmit={handleSubmit}
      placeholder="Add comment..."
      disabled={disabled}
      submitting={submitting}
      showLinkToMove={currentMove !== '-1'} // Hide link option at start position
      currentMoveNotation={currentMoveNotation}
      rateLimitError={rateLimitError}
      style={{
        padding: '6px',
        gap: '6px'
      }}
    />
  );
};

// Variation utility functions
const hasVariations = (move) => {
  return move?.variations && move.variations.length > 0;
};

const isCommentMove = (move) => {
  if (!move || !move.creator) return false;
  return move.creator.type === CHESSBOARD.MOVE_TYPES.COMMENT;
};

// Helper to check if a move has annotations (content)
const hasAnnotations = (move) => {
  return move?.content && move.content.length > 0;
};

// Helper to check if a move is starred
const isStarred = (moveId, starredMoves) => {
  if (!starredMoves || !Array.isArray(starredMoves)) return false;
  return starredMoves.includes(moveId);
};

// Helper to get variation move content by path
const getVariationMoveContent = (moveId, moves, state) => {
  try {
    // Use VariationPathUtils to navigate to the move
    const result = getNodeAtPathInfinite(moves, moveId, state);
    if (result.success && result.node) {
      return result.node.content || null;
    }
  } catch (error) {
    console.error('Error getting variation move content:', error);
  }
  return null;
};

// Helper functions to check if current move has customizations
const currentMoveHasSquares = (currentMove, state) => {
  if (currentMove === '-1') {
    const squares = state?.startPosition?.highlightedSquares;
    return squares && Object.keys(squares).length > 0;
  } else if (currentMove && !currentMove.includes('|')) {
    const moveIndex = parseInt(currentMove, 10);
    const squares = state?.moves?.[moveIndex]?.highlightedSquares;
    return squares && Object.keys(squares).length > 0;
  }
  return false;
};

const currentMoveHasArrows = (currentMove, state) => {
  if (currentMove === '-1') {
    const arrows = state?.startPosition?.arrows;
    return arrows && arrows.length > 0;
  } else if (currentMove && !currentMove.includes('|')) {
    const moveIndex = parseInt(currentMove, 10);
    const arrows = state?.moves?.[moveIndex]?.arrows;
    return arrows && arrows.length > 0;
  }
  return false;
};

// Unified styling constants - Main Line standards
const MOVE_BUTTON_CONSTANTS = {
  padding: '6px 10px',
  margin: '2px',
  fontSize: '13px',
  borderRadius: '4px',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  minHeight: '32px',
  display: 'flex',
  alignItems: 'center'
};

const MOVE_CONTAINER_CONSTANTS = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  gap: '8px',
  overflowX: 'auto',
  scrollBehavior: 'smooth',
  width: '100%',
  maxWidth: '100%'
};

// Enhanced helper to get move styling for ALL variation types
const getMoveButtonStyle = (move, index, isCurrentMove, isInVariation, hasVariations, starredMoves = [], variationType = 'main', hasCommentMoves = false) => {
  const moveId = String(index);
  const hasAnnotation = hasAnnotations(move);
  const starred = isStarred(moveId, starredMoves);
  
  // Base colors - standardized for ALL sections
  let backgroundColor, textColor, borderLeft = 'none', borderBottom = 'none', borderRight = 'none', borderTop = 'none';
  
  if (isCurrentMove) {
    // Current move - ALWAYS green background with black text (universal standard)
    backgroundColor = '#58b047';
    textColor = 'black';
  } else {
    // Non-current moves - ALWAYS gray background with light gray text (universal standard)
    backgroundColor = '#333';
    textColor = '#ccc';
    
    if (variationType === 'main') {
      // Main line moves get light grey border (using individual properties to avoid conflicts)
      borderLeft = '2px solid #666';
      borderRight = '2px solid #666';
      borderTop = '2px solid #666';
      borderBottom = '2px solid #666';
      
      if (hasVariations) {
        // Main line moves with variations get bottom border (50% opacity) - override the bottom
        const hasCommentVariation = move.variations?.some(variation => 
          variation.some(varMove => varMove?.creator?.type === CHESSBOARD.MOVE_TYPES.COMMENT)
        );
        borderBottom = hasCommentVariation ? '2px solid rgba(255, 102, 0, 0.5)' : '2px solid rgba(255, 215, 0, 0.5)';
      }
    } else if (variationType === 'variation' || variationType === 'preview') {
      // Variation moves get ONLY their gold/orange border (comment = orange, regular = gold) at 50% opacity
      // Only borderLeft and borderBottom to match Main Line pattern
      const variationBorder = hasCommentMoves ? '2px solid rgba(255, 102, 0, 0.5)' : '2px solid rgba(255, 215, 0, 0.5)';
      borderLeft = variationBorder;
      borderBottom = variationBorder;
    }
  }
  
  // Add green left border for annotations (universal standard) - this will override the left border set above
  if (hasAnnotation && !isCurrentMove) {
    borderLeft = '3px solid #58b047';
  }
  
  return {
    ...MOVE_BUTTON_CONSTANTS,
    backgroundColor,
    color: textColor,
    borderLeft,
    borderRight,
    borderTop,
    borderBottom,
    starred
  };
};

// Component to display variation move content
const VariationContentDisplay = ({ currentMove, moves, state }) => {
  let content = null;
  let isComment = false;
  
  // Handle different move types
  if (currentMove === '-1') {
    // Start position - no content to show
    return null;
  } else if (currentMove.includes('|')) {
    // Variation move - use the helper to get content
    content = getVariationMoveContent(currentMove, moves, state);
    const result = getNodeAtPathInfinite(moves, currentMove, state);
    isComment = result.success && isCommentMove(result.node);
  } else {
    // Main line move - get content directly
    const moveIndex = parseInt(currentMove, 10);
    if (moveIndex >= 0 && moveIndex < moves.length) {
      content = moves[moveIndex]?.content || null;
      isComment = isCommentMove(moves[moveIndex]);
    }
  }
  
  if (!content || content.length === 0) {
    return null;
  }
  
  return (
    <div style={{
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: '#222',
      borderLeft: '3px solid #58b047',
      borderRadius: '4px',
      fontSize: '13px',
      color: '#eee',
      lineHeight: '1.4',
      maxWidth: '100%',
      wordBreak: 'break-word'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '4px',
        fontSize: '11px',
        color: '#999',
        fontWeight: 'bold'
      }}>
        <i className={isComment ? 'fa-solid fa-comment' : 'fa-solid fa-sticky-note'} />
        <span>{isComment ? 'Comment' : 'Annotation'}</span>
        {currentMove.includes('|') && (
          <span style={{ 
            color: '#ffd700', 
            fontSize: '10px',
            backgroundColor: '#444',
            padding: '1px 4px',
            borderRadius: '2px'
          }}>
            Variation
          </span>
        )}
      </div>
      <div style={{ fontSize: '13px', color: '#eee' }}>
        {content}
      </div>
    </div>
  );
};

const getCurrentMoveVariations = (currentMoveId, moves, state) => {
  // Use robust path parsing for infinite nesting support
  const pathSegments = parseVariationPath(currentMoveId);
  const inVariation = pathSegments.length > 1 || currentMoveId.includes('|');
  
  // Get the current node to check for variations
  let currentNode, currentNodeResult;
  
  if (currentMoveId === '-1') {
    // Start position - check for start position variations
    if (!state?.startPosition?.variations || state.startPosition.variations.length === 0) {
      return null;
    }
    return {
      mode: state.startPosition.variations.length === 1 ? 'single' : 'multiple',
      parentMove: { notation: '⧫' },
      parentId: '-1',
      variations: state.startPosition.variations,
      isStartPosition: true,
      inVariation: false,
      depth: 0
    };
  }
  
  // Use VariationPathUtils to get current node
  currentNodeResult = getNodeAtPathInfinite(moves, currentMoveId, state);
  
  if (!currentNodeResult.success) {
    console.warn('Failed to get node at path:', currentMoveId, currentNodeResult.error);
    return null;
  }
  
  currentNode = currentNodeResult.node;
  
  if (inVariation) {
    // We're inside a variation - check for:
    // 1. Sub-variations from current position
    // 2. Sibling variations at the same level
    
    const currentDepth = getPathDepth(currentMoveId);
    let variationsToShow = [];
    
    // First, check if current move has its own variations (sub-variations)
    if (hasVariations(currentNode)) {
      const result = {
        mode: currentNode.variations.length === 1 ? 'single' : 'multiple',
        parentMove: currentNode,
        parentId: currentMoveId,
        variations: currentNode.variations,
        isStartPosition: false,
        inVariation: true,
        depth: currentDepth + 1, // These would be one level deeper
        isSubVariation: true
      };
      return result;
    }
    
    // If no sub-variations, check for sibling variations
    const parentPath = getParentPath(currentMoveId);
    if (parentPath) {
      const parentResult = getNodeAtPathInfinite(moves, parentPath, state);
      if (parentResult.success && hasVariations(parentResult.node)) {
        // Get the current variation index to find siblings
        const lastSegment = pathSegments[pathSegments.length - 1];
        const currentVarIndex = lastSegment.type === 'variation' ? lastSegment.index : null;
        
        if (currentVarIndex !== null) {
          const allVariations = parentResult.node.variations;
          const siblingVariations = allVariations.filter((_, index) => index !== currentVarIndex);
          
          if (siblingVariations.length > 0) {
            const siblingIndexes = allVariations.map((_, index) => index).filter(index => index !== currentVarIndex);
            
            const result = {
              mode: allVariations.length <= 2 ? 'single' : 'multiple',
              parentMove: parentResult.node,
              parentId: parentPath,
              variations: siblingVariations,
              siblingIndexes: siblingIndexes,
              isStartPosition: parentPath === '-1',
              inVariation: true,
              depth: currentDepth,
              isSiblingVariation: true
            };
            return result;
          }
        }
      }
    }
    
    return null; // No variations found
    
  } else {
    // We're on main line - show variations for current move
    if (!hasVariations(currentNode)) {
      return null;
    }
    
    const result = {
      mode: currentNode.variations.length === 1 ? 'single' : 'multiple',
      parentMove: currentNode,
      parentId: currentMoveId,
      parentIndex: parseInt(currentMoveId, 10),
      variations: currentNode.variations,
      isStartPosition: false,
      inVariation: false,
      depth: 1 // First level variations
    };
    return result;
  }
};

// Variation row renderer component
const renderVariationRow = (variationData, goToMove, currentMove, state, variationMoveRefs = null) => {
  if (!variationData) return null;

  const { mode, parentMove, parentId, variations, siblingIndexes, isStartPosition, inVariation } = variationData;
  
  // Add comprehensive error checking
  if (!variations || !Array.isArray(variations) || variations.length === 0) {
    console.warn('renderVariationRow: No valid variations found', variationData);
    return null;
  }
  
  if (mode === 'single') {
    // Single variation mode - show full variation line
    const variation = variations[0];
    const actualVarIndex = inVariation ? (siblingIndexes?.[0] ?? 0) : 0;
    
    if (!variation || variation.length === 0) return null;
    
    const hasCommentMoves = variation.some(varMove => isCommentMove(varMove));
    const baseColor = hasCommentMoves ? 'rgba(255, 102, 0, 0.5)' : 'rgba(255, 215, 0, 0.5)';
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', }}>
        <span style={{ 
          color: 'baseColor',
          fontSize: '14px',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          paddingLeft: '0px'
        }}>
          <i class="fa-solid fa-code-branch fa-rotate-90" style={{ fontSize: '16px', color: 'gold', paddingLeft: '5px',  }}></i>
        </span>
        <div 
          className="mobile-moves-scrollbar"
          style={{
            ...MOVE_CONTAINER_CONSTANTS,
            // paddingTop: '4px',
            // paddingLeft: '4px',
            flex: 1
          }}
      >
        {/* Render all moves in the variation */}
        {variation.map((varMove, moveIndex) => {
          const moveId = isStartPosition ? 
            `-1|${actualVarIndex}:${moveIndex}` : 
            `${parentId}|${actualVarIndex}:${moveIndex}`;
          
          const isCurrentMove = currentMove === moveId;
          
          // Calculate correct move number continuing from parent position
          let moveNumber;
          let isWhiteMove;
          
          if (isStartPosition) {
            // Start position variations begin from move 1
            moveNumber = Math.floor(moveIndex / 2) + 1;
            isWhiteMove = moveIndex % 2 === 0;
          } else {
            // Continue numbering from parent move using cumulative calculation
            const parentMoveIndex = calculateCumulativeMoveIndex(parentId);
            const totalMoveIndex = parentMoveIndex + 1 + moveIndex; // +1 because variation starts from next move
            moveNumber = Math.floor(totalMoveIndex / 2) + 1;
            isWhiteMove = totalMoveIndex % 2 === 0;
          }
          
          const styling = getMoveButtonStyle(varMove, moveIndex, isCurrentMove, true, false, state?.starredMoves, 'preview', hasCommentMoves);
          
          return (
            <button
              key={moveIndex}
              ref={variationMoveRefs ? (el) => {
                if (el) {
                  variationMoveRefs.current.set(`variation-${moveId}`, el);
                }
              } : undefined}
              onClick={() => {
                // console.log('🎯 Clicking variation move:', moveId);
                goToMove(moveId);
              }}
              style={{
                ...styling,
                position: 'relative'
              }}
              title={`${hasCommentMoves ? 'Comment ' : ''}Variation move: ${varMove.notation}`}
            >
                  {/* Gold star for starred variation moves */}
                  {isStarred(moveId, state?.starredMoves) && (
                    <i 
                      className="fa-solid fa-star" 
                      style={{
                        color: '#ffd700',
                        fontSize: '10px',
                        marginRight: '2px',
                        verticalAlign: 'top'
                      }}
                    />
                  )}
                  {/* Move number inside button for white moves or first black move */}
                  {(isWhiteMove || (!isWhiteMove && moveIndex === 0)) && (
                    <span style={{ marginRight: '4px', opacity: 0.7 }}>
                      {isWhiteMove ? `${moveNumber}.` : `${moveNumber}...`}
                    </span>
                  )}
                  {varMove.notation}
                </button>
          );
        })}
        </div>
      </div>
    );
  } else {
    // Multiple variations mode - show preview (first move of each)
    const variationsToShow = variations.map((variation, localIndex) => {
      if (!variation || !Array.isArray(variation) || variation.length === 0) return null;
      
      const actualVarIndex = inVariation ? (siblingIndexes?.[localIndex] ?? localIndex) : localIndex;
      const hasCommentMoves = variation.some(varMove => isCommentMove(varMove));
      
      return {
        variation,
        varIndex: actualVarIndex,
        firstMove: variation[0],
        hasCommentMoves,
        moveId: isStartPosition ? `-1|${actualVarIndex}:0` : `${parentId}|${actualVarIndex}:0`
      };
    }).filter(Boolean);
    
    if (variationsToShow.length === 0) return null;

    // Determine label color: gold if there are regular variations, orange if only comments
    const hasRegularVariations = variationsToShow.some(varInfo => !varInfo.hasCommentMoves);
    const labelColor = hasRegularVariations ? 'rgba(255, 215, 0, 0.5)' : 'rgba(255, 102, 0, 0.5)';

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingLeft: '3px'
        }}>
          {/* {parentMove.notation}<br /> */}
          <span style={{
            fontSize: '15px',
            fontWeight: 'bold',
            color: 'rgba(255, 102, 0, 0.5)',
            lineHeight: '1',
            margin: '0'
          }}>
            {variationsToShow.length}
          </span>
          <i class="fa-solid fa-code-branch fa-rotate-90"
          style={{ fontSize: '13px', color: 'rgba(255, 102, 0, 0.5)', margin: '0' }}></i>
        </div>
        <div 
          className="mobile-moves-scrollbar"
          style={{
            ...MOVE_CONTAINER_CONSTANTS,
            // paddingTop: '2px',
            flex: 1,
       
          }}
        >
        {/* Parent move button */}
        {/* <button
          onClick={() => goToMove(parentId)}
          style={{
            padding: '4px 8px',
            margin: '2px',
            backgroundColor: '#444',
            color: '#58b047',
            border: '2px solid #58b047',
            borderRadius: '4px',
            fontSize: '11px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            minWidth: '40px',
            transition: 'all 0.2s ease',
            marginLeft: '8px'
          }}
          title={`Parent move: ${parentMove.notation}`}
        >
          {parentMove.notation}
        </button> */}
        
        {/* Arrow separator */}
        {/* <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          color: '#666',
          fontSize: '12px',
          paddingTop: '2px',
          
        }}>
          →
        </div> */}
        
        {/* Variation previews */}
        {variationsToShow.map((varInfo, index) => {
          const isCurrentVariation = currentMove.includes('|') && 
            currentMove.startsWith(varInfo.moveId.split(':')[0]);
          
          // Calculate correct move number for this variation's first move
          let moveNumber;
          let isWhiteMove;
          
          if (isStartPosition) {
            // Start position variations begin from move 1
            moveNumber = 1;
            isWhiteMove = true;
          } else {
            // Continue numbering from parent move using cumulative calculation
            const parentMoveIndex = calculateCumulativeMoveIndex(parentId);
            const totalMoveIndex = parentMoveIndex + 1; // First move of variation is next after parent
            moveNumber = Math.floor(totalMoveIndex / 2) + 1;
            isWhiteMove = totalMoveIndex % 2 === 0;
          }
          
          // For multi-variation preview, all buttons are non-current (they're previews of unplayed variations)
          const variationBorder = varInfo.hasCommentMoves ? '2px solid rgba(255, 102, 0, 0.5)' : '2px solid rgba(255, 215, 0, 0.5)';
          const customStyling = {
            ...MOVE_BUTTON_CONSTANTS,
            backgroundColor: '#333',
            color: '#ccc',
            borderLeft: variationBorder,
            borderBottom: variationBorder,
            borderRight: 'none',
            borderTop: 'none'
          };
          
          // Add green left border for annotations if needed (overrides variation border)
          if (hasAnnotations(varInfo.firstMove)) {
            customStyling.borderLeft = '3px solid #58b047';
          }
          
          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', }}>
              {/* THIS IS THE previewVariationMoveButtons for 2+ variations with parent move + arrow */}
              <button
                onClick={() => {
                  // console.log('🎯 Clicking preview variation move:', varInfo.moveId);
                  goToMove(varInfo.moveId);
                }}
                style={customStyling}
                title={`${varInfo.hasCommentMoves ? 'Comment ' : ''}Variation: ${varInfo.firstMove.notation}`}
              >
                {isWhiteMove ? `${moveNumber}.` : `${moveNumber}...`} {varInfo.firstMove.notation}
              </button>
            </div>
          );
        })}
        </div>
      </div>
    );
  }
};

// Helper to get the current variation line when we're inside a variation (handles infinite nesting)
const getCurrentVariationLine = (currentMoveId, moves, state) => {
  if (!currentMoveId.includes('|')) return null;
  
  // Use robust path parsing for nested variations
  const pathSegments = parseVariationPath(currentMoveId);
  
  // We need at least one variation segment to be in a variation
  const variationSegments = pathSegments.filter(seg => seg.type === 'variation');
  if (variationSegments.length === 0) return null;
  
  // Get the current variation array and parent information
  // For nested paths like "4|0:1|1:2", we need to find the variation array
  // that the current position is in
  
  // Get the parent path (everything except the last segment)
  let parentPath;
  let currentVariationArray = null;
  let currentVarIndex = null;
  let parentMove = null;
  let parentNotation = '';
  
  if (pathSegments.length === 2) {
    // Single level variation like "4|0:1"
    const mainSeg = pathSegments[0];
    const varSeg = pathSegments[1];
    
    parentPath = String(mainSeg.index);
    currentVarIndex = varSeg.index;
    
    if (mainSeg.index === -1) {
      // Start position variation
      if (!state?.startPosition?.variations || !state.startPosition.variations[currentVarIndex]) {
        return null;
      }
      currentVariationArray = state.startPosition.variations[currentVarIndex];
      parentNotation = 'Start';
      parentMove = { notation: '⧫' };
    } else {
      // Main line variation
      if (mainSeg.index < 0 || mainSeg.index >= moves.length) return null;
      parentMove = moves[mainSeg.index];
      if (!parentMove.variations || !parentMove.variations[currentVarIndex]) return null;
      currentVariationArray = parentMove.variations[currentVarIndex];
      parentNotation = `After ${parentMove.notation}`;
    }
  } else {
    // Nested variation like "4|0:1|1:2" - we're inside a sub-variation
    const parentSegments = pathSegments.slice(0, -1);
    const lastVarSegment = pathSegments[pathSegments.length - 1];
    
    parentPath = pathSegments.slice(0, -1).reduce((path, seg, index) => {
      if (index === 0) return String(seg.index);
      return path + `|${seg.index}:${seg.moveIndex}`;
    }, '');
    
    currentVarIndex = lastVarSegment.index;
    
    // Use VariationPathUtils to navigate to the parent node
    const parentResult = getNodeAtPathInfinite(moves, parentPath, state);
    if (!parentResult.success) {
      console.warn('Failed to get parent node for nested variation:', parentPath);
      return null;
    }
    
    parentMove = parentResult.node;
    if (!parentMove.variations || !parentMove.variations[currentVarIndex]) {
      return null;
    }
    
    currentVariationArray = parentMove.variations[currentVarIndex];
    
    // Build a descriptive parent notation for nested variations
    const depth = getPathDepth(currentMoveId);
    parentNotation = `Var ${depth} After ${parentMove.notation}`;
  }
  
  if (!currentVariationArray) return null;
  
  const hasCommentMoves = currentVariationArray.some(varMove => isCommentMove(varMove));
  
  const result = {
    variation: currentVariationArray,
    varIndex: currentVarIndex,
    parentMoveId: parentPath,
    parentMove,
    parentNotation,
    hasCommentMoves,
    baseColor: hasCommentMoves ? 'rgba(255, 102, 0, 0.5)' : 'rgba(255, 215, 0, 0.5)',
    depth: getPathDepth(currentMoveId)
  };
  
  return result;
};

// Helper to get the end of current line (last move before branching)
const getEndOfCurrentLine = (currentMoveId, moves, state) => {
  if (currentMoveId === '-1') {
    // At start position, go to first move if available
    return moves.length > 0 ? '0' : null;
  }
  
  if (!currentMoveId.includes('|')) {
    // On main line - go to next main move
    const currentIndex = parseInt(currentMoveId, 10);
    if (!isNaN(currentIndex) && currentIndex < moves.length - 1) {
      return String(currentIndex + 1);
    }
    return null; // Already at end
  }
  
  // In a variation - find the end of current variation
  const pathSegments = parseVariationPath(currentMoveId);
  if (pathSegments.length === 0) return null;
  
  // Get the current variation array
  const currentResult = getNodeAtPathInfinite(moves, currentMoveId, state);
  if (!currentResult.success) return null;
  
  // Find the variation array we're currently in
  let currentVariationArray = null;
  let basePathForVariation = '';
  
  if (pathSegments.length === 2) {
    // Single level variation like "4|0:1"
    const mainSeg = pathSegments[0];
    const varSeg = pathSegments[1];
    
    if (mainSeg.index === -1) {
      currentVariationArray = state?.startPosition?.variations?.[varSeg.index];
      basePathForVariation = `-1|${varSeg.index}`;
    } else {
      const parentMove = moves[mainSeg.index];
      currentVariationArray = parentMove?.variations?.[varSeg.index];
      basePathForVariation = `${mainSeg.index}|${varSeg.index}`;
    }
  } else {
    // Nested variation - get parent node and then its variation
    const parentSegments = pathSegments.slice(0, -1);
    const lastVarSegment = pathSegments[pathSegments.length - 1];
    
    const parentPath = parentSegments.reduce((path, seg, index) => {
      if (index === 0) return String(seg.index);
      return path + `|${seg.index}:${seg.moveIndex}`;
    }, '');
    
    const parentResult = getNodeAtPathInfinite(moves, parentPath, state);
    if (parentResult.success) {
      currentVariationArray = parentResult.node?.variations?.[lastVarSegment.index];
      basePathForVariation = parentPath + `|${lastVarSegment.index}`;
    }
  }
  
  if (!currentVariationArray || currentVariationArray.length === 0) return null;
  
  // Return path to last move in this variation
  const lastMoveIndex = currentVariationArray.length - 1;
  return `${basePathForVariation}:${lastMoveIndex}`;
};

// Helper to create breadcrumb trail
const createBreadcrumbTrail = (currentMoveId, moves, state) => {
  if (currentMoveId === '-1') {
    return [{ label: 'Start', path: '-1', isClickable: true }];
  }
  
  if (!currentMoveId.includes('|')) {
    // Main line
    return [
      { label: 'Main', path: '-1', isClickable: true },
      { label: `Move ${parseInt(currentMoveId) + 1}`, path: currentMoveId, isClickable: true }
    ];
  }
  
  // In variation - build full breadcrumb trail
  const pathSegments = parseVariationPath(currentMoveId);
  const breadcrumbs = [];
  
  let currentPath = '';
  let isFirstMainMove = true;
  
  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    
    if (segment.type === 'main') {
      if (segment.index === -1) {
        // Start position - handle later
        currentPath = '-1';
      } else {
        currentPath = String(segment.index);
        const move = moves[segment.index];
        if (move && isFirstMainMove) {
          // Combine "Main" with first move notation
          breadcrumbs.push({
            // label: `fa-icon ${move.notation}`,
            label: (
              <>
                <i className="fa-solid fa-home" style={{ marginRight: '4px', fontSize: '14px' }}></i>
                {move.notation}
              </>
            ),

            path: currentPath,
            isClickable: true,
            isMove: true
          });
          isFirstMainMove = false;
        } else if (move) {
          breadcrumbs.push({
            label: move.notation,
            path: currentPath,
            isClickable: true,
            isMove: true
          });
        }
      }
    } else if (segment.type === 'variation') {
      // Add variation indicator with branch icon - always show first move of variation (branching point)
      const branchingPointPath = currentPath + `|${segment.index}:0`; // Always use :0 for branching point
      currentPath += `|${segment.index}:${segment.moveIndex}`; // Keep actual path for navigation context
      
      // Get the FIRST move of this variation for the label (the branching point)
      const nodeResult = getNodeAtPathInfinite(moves, branchingPointPath, state);
      
      // Check if this variation contains comment moves
      let isCommentVariation = false;
      try {
        // Get the variation array to check for comment moves
        const pathSegments = branchingPointPath.split('|');
        let variation = null;
        
        if (pathSegments.length === 2) {
          // First level variation
          const mainMoveIdx = parseInt(pathSegments[0], 10);
          const [varIdx] = pathSegments[1].split(':').map(Number);
          
          if (mainMoveIdx === -1) {
            variation = state?.startPosition?.variations?.[varIdx];
          } else {
            const mainMove = moves[mainMoveIdx];
            variation = mainMove?.variations?.[varIdx];
          }
        } else {
          // Nested variation - get parent and then variation
          const parentPath = pathSegments.slice(0, -1).join('|');
          const parentResult = getNodeAtPathInfinite(moves, parentPath, state);
          if (parentResult.success && parentResult.node?.variations) {
            const [varIdx] = pathSegments[pathSegments.length - 1].split(':').map(Number);
            variation = parentResult.node.variations[varIdx];
          }
        }
        
        if (variation && variation.length > 0) {
          isCommentVariation = variation.some(varMove => isCommentMove(varMove));
        }
      } catch (error) {
        console.error('Error checking comment variation in breadcrumb:', error);
      }
      
      if (nodeResult.success && nodeResult.node) {
        breadcrumbs.push({
          label: nodeResult.node.notation,
          icon: 'fa-code-branch',
          path: branchingPointPath, // Click goes to the branching point, not current move
          isClickable: true,
          isVariation: true,
          isCommentVariation: isCommentVariation,
          varIndex: segment.index,
          depth: i
        });
      } else {
        breadcrumbs.push({
          label: '',
          icon: 'fa-code-branch',
          path: branchingPointPath,
          isClickable: true,
          isVariation: true,
          isCommentVariation: isCommentVariation,
          varIndex: segment.index,
          depth: i
        });
      }
    }
  }
  
  // If no main move was found (start position variation), add "Main" by itself
  if (isFirstMainMove) {
    breadcrumbs.unshift({ label: 'Main', path: '-1', isClickable: true });
  }
  
  return breadcrumbs;
};

// Breadcrumb component
const renderBreadcrumbs = (currentMoveId, moves, state, goToMove) => {
  const breadcrumbs = createBreadcrumbTrail(currentMoveId, moves, state);
  
  if (breadcrumbs.length <= 1) return null;
  
  return (
    <div 
      className="mobile-moves-scrollbar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'nowrap',
        fontSize: '11px'
      }}>
      {breadcrumbs.map((crumb, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <i className="fa-solid fa-code-branch" style={{ 
              color: '#888', 
              fontSize: '10px', 
              margin: '0 4px',
              transform: 'rotate(90deg)'
            }}></i>
          )}
          <button
            onClick={() => crumb.isClickable && goToMove(crumb.path)}
            style={{
              background: crumb.isVariation 
                ? (crumb.isCommentVariation ? 'rgba(255, 102, 0)' : 'rgba(255, 215, 0)') // Orange for comment variations, Gold for regular variations
                : 'rgba(87, 176, 71)', // Green background for main moves in breadcrumb - same opacity as variations
              border: (() => {
                // Check if current move is within this breadcrumb's variation path
                if (crumb.isVariation) {
                  // Extract the variation path from the breadcrumb (e.g., "5|0:0" -> "5|0")
                  const breadcrumbVariationPath = crumb.path.split(':')[0];
                  // Check if current move starts with this variation path
                  return currentMoveId.startsWith(breadcrumbVariationPath + ':') ? '2px solid #000' : 'none';
                } else {
                  // For main line breadcrumbs, check if we're exactly on that move
                  return crumb.path === currentMoveId ? '2px solid #000' : '2px solid black';
                }
              })(),
              color: crumb.isVariation 
                ? '#1a1a1a' // Dark text on colored backgrounds
                : 'rgb(26, 26, 26)',
              cursor: crumb.isClickable ? 'pointer' : 'default',
              fontSize: '14px',
              padding: '6px 10px', // Same padding for all breadcrumb buttons
              borderRadius: '6px',
              textDecoration: 'none',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
              // boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onMouseOver={(e) => {
              if (crumb.isClickable) {
                if (crumb.isVariation) {
                  e.target.style.backgroundColor = crumb.isCommentVariation ? 'rgba(255, 102, 0, 1)' : 'rgba(255, 215, 0, 1)';
                } else {
                  e.target.style.backgroundColor = 'rgba(87, 176, 71, 1)'; // Brighter green hover for main moves in breadcrumb
                }
              }
            }}
            onMouseOut={(e) => {
              if (crumb.isVariation) {
                e.target.style.backgroundColor = crumb.isCommentVariation ? 'rgba(255, 102, 0, 0.8)' : 'rgba(255, 215, 0, 0.8)';
              } else {
                e.target.style.backgroundColor = 'rgba(87, 176, 71, 0.8)'; // Reset to green background for main moves in breadcrumb
              }
            }}
            title={`Go to ${crumb.label}`}
            disabled={!crumb.isClickable}
          >
            {crumb.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

// CSS for gold scrollbars
const scrollbarStyles = `
  .mobile-moves-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: #ffd700 transparent;
  }
  
  .mobile-moves-scrollbar::-webkit-scrollbar {
    height: 6px;
  }
  
  .mobile-moves-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .mobile-moves-scrollbar::-webkit-scrollbar-thumb {
    background-color: #ffd700;
    border-radius: 3px;
  }
  
  .mobile-moves-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: #ffed4e;
  }
    
`;

// Inject styles on component mount
if (typeof document !== 'undefined' && !document.getElementById('mobile-moves-scrollbar-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'mobile-moves-scrollbar-styles';
  styleSheet.textContent = scrollbarStyles;
  document.head.appendChild(styleSheet);
}

// Navigation Controls Component - Separate export for flexible positioning
export const MobileNavigationControls = ({ 
  // Comment mode props for navigation
  isCommentMode = false,
  commentMoves = [],
  currentCommentMoveIndex = -1,
  onCommentMoveClick = null,
  onTriggerExit = null,
  onConfirmDelete = null,
  // Callback for custom goToMove function (e.g., from ViewGame)
  goToMoveCallback = null,
  // Override current move and moves from parent (for state sync in ViewGame)
  currentMoveOverride = null,
  movesOverride = null,
  // Puzzle mode props for restricting navigation
  gameHasPuzzleTag = false,
  puzzleComplete = false,
  // Record mode autoplay integration
  toggleAutoPlayCallback = null,
  autoPlayState = null
} = {}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Use external autoplay state if provided (for Record mode integration)
  const effectiveIsPlaying = autoPlayState !== null ? autoPlayState : isPlaying;
  
  const { moves: gameLogicMoves, currentMove: gameLogicCurrentMove, goToMove } = useGameLogic();
  const { state } = useGameState();
  
  // Use override values when provided (for ViewGame state sync)
  const moves = movesOverride || gameLogicMoves;
  const currentMove = currentMoveOverride || gameLogicCurrentMove;
  
  // Debug the context values (commented out to reduce console spam)
  // console.log('🎬 MobileNavigationControls context debug:', {
  //   movesLength: moves?.length,
  //   currentMove,
  //   currentMoveOverride,
  //   movesFirstThree: moves?.slice(0, 3),
  //   goToMoveType: typeof goToMove,
  //   stateCurrentMove: state.currentMove,
  //   usingOverrides: !!currentMoveOverride || !!movesOverride
  // });
  
  // Parse current move to get index - use comment moves when in comment mode
  const currentMoveIndex = isCommentMode ? 
    currentCommentMoveIndex : 
    (currentMove === '-1' ? -1 : parseInt(currentMove) || 0);
  
  // Use ref to track current move for interval closure
  const currentMoveRef = useRef(currentMove);
  currentMoveRef.current = currentMove;
  
  // Use comment moves or main moves based on mode
  const workingMoves = isCommentMode ? commentMoves : moves;
  
  // Also track current move index in ref
  const currentMoveIndexRef = useRef(currentMoveIndex);
  const workingMovesRef = useRef(workingMoves);
  currentMoveIndexRef.current = currentMoveIndex;
  const isInVariation = currentMove.includes('|');
  const workingMovesLength = workingMoves.length;
  
  // Check if navigation should be disabled due to puzzle mode
  const isPuzzleNavigationDisabled = gameHasPuzzleTag && !puzzleComplete && !isCommentMode;
  
  // Remove duplicate - refs already declared above
  // Update current values in refs
  currentMoveIndexRef.current = currentMoveIndex;
  workingMovesRef.current = workingMoves;
  
  // Auto-play functionality - plays through all main line moves
  useEffect(() => {
    const effectId = Math.random().toString(36).substr(2, 9);
    // console.log(`🎬 [${effectId}] MobileNavigationControls Auto-play useEffect triggered:`, {
    //   isPlaying,
    //   currentMoveIndex,
    //   movesLength: workingMoves.length,
    //   canPlay: isPlaying && currentMoveIndex < workingMoves.length - 1,
    //   isCommentMode
    // });
    
    // Skip basic autoplay if using external Record mode autoplay
    if (toggleAutoPlayCallback) {
      return;
    }
    
    let interval;
    if (isPlaying) {
      if (currentMoveIndexRef.current >= workingMovesRef.current.length - 1) {
        // At the end, stop playing
        // console.log(`🎬 [${effectId}] MobileNavigationControls Auto-play stopping - at end`);
        setIsPlaying(false);
        return;
      }
      
      // console.log(`🎬 [${effectId}] MobileNavigationControls Starting auto-play interval`);
      interval = setInterval(() => {
        const currentIndex = currentMoveIndexRef.current;
        const currentWorkingMoves = workingMovesRef.current;
        const nextIndex = currentIndex + 1;
        // console.log('🎬 MobileNavigationControls Auto-play advancing from', currentIndex, 'to', nextIndex, 'workingMovesLength:', currentWorkingMoves.length);
        
        if (nextIndex < currentWorkingMoves.length) {
          if (isCommentMode && onCommentMoveClick) {
            // Use comment move navigation
            // console.log('🎬 Calling onCommentMoveClick with:', nextIndex);
            onCommentMoveClick(nextIndex);
          } else {
            // Use regular navigation - prioritize callback prop over useGameLogic
            const moveFunction = goToMoveCallback || goToMove;
            // console.log('🎬 Calling goToMove with:', String(nextIndex), 'using', goToMoveCallback ? 'callback prop' : 'useGameLogic');
            try {
              const result = moveFunction(String(nextIndex));
              // console.log('🎬 goToMove result:', result);
            } catch (error) {
              console.error('🎬 goToMove error:', error);
              setIsPlaying(false);
            }
          }
        } else {
          // Reached the end, stop playing
          // console.log('🎬 MobileNavigationControls Auto-play reached end, stopping');
          setIsPlaying(false);
        }
      }, 1000); // 1 second interval between moves
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying]);
  
  // Handle play button click
  const handlePlayClick = () => {
    // console.log('🎬 MobileNavigationControls Play button clicked:', {
    //   currentMoveIndex,
    //   movesLength: moves.length,
    //   currentMove,
    //   isPlaying,
    //   moves: moves.slice(0, 3) // First 3 moves for debugging
    // });
    
    // Use Record mode callback if available
    if (toggleAutoPlayCallback) {
      console.log('[DEBUG] Using Record mode autoplay callback');
      toggleAutoPlayCallback();
      return;
    }
    
    if (currentMoveIndex >= workingMoves.length - 1) {
      // At the end, restart from beginning
      // console.log('🎬 Restarting from beginning');
      if (isCommentMode && onCommentMoveClick) {
        onCommentMoveClick(-1);
      } else {
        const moveFunction = goToMoveCallback || goToMove;
        moveFunction('-1');
      }
      setTimeout(() => setIsPlaying(true), 100);
    } else {
      // Toggle play state
      // console.log('🎬 Toggling play state to:', !isPlaying);
      setIsPlaying(!isPlaying);
    }
  };

  // Don't render if no moves (but this shouldn't happen since parent checks)
  // Removed the early return to allow navigation controls even with 0 moves

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      backgroundColor: '#0a0a0a', // Red-tinted dark background
      width: '100%',
      boxSizing: 'border-box',
      borderRadius: '10px',
    }}>
      {/* First Move Button - Context aware */}
      <button
        onClick={() => {
          setIsPlaying(false);
          if (isCommentMode && onCommentMoveClick) {
            // Comment mode navigation
            if (currentCommentMoveIndex === -1) {
              // Already at START position, directly delete comment state instead of showing confirmation
              // console.log('🚨 First button: Already at START position, auto-deleting comment state');
              onConfirmDelete && onConfirmDelete();
            } else {
              // Navigate to START of comment variation
              onCommentMoveClick(-1);
            }
          } else if (isInVariation) {
            // Jump to parent move that spawned this variation
            const parentPath = getParentPath(currentMove);
            if (parentPath) {
              goToMove(parentPath);
            }
          } else {
            // Main line - go to start position
            goToMove('-1');
          }
        }}
        disabled={(() => {
          if (isPuzzleNavigationDisabled) {
            return true; // Disabled in puzzle mode until complete
          } else if (isCommentMode) {
            return false; // Always enabled in comment mode
          } else if (isInVariation) {
            const parentPath = getParentPath(currentMove);
            return !parentPath;
          } else {
            return currentMove === '-1';
          }
        })()}
        style={{
          padding: '14px 6px',
          backgroundColor: 'transparent',
          color: (() => {
            if (isPuzzleNavigationDisabled) {
              return '#333'; // Disabled color for puzzle mode
            } else if (isCommentMode) {
              // Red when at START position (boundary), gold otherwise
              return currentCommentMoveIndex === -1 ? '#ff4444' : 'gold';
            } else if (isInVariation) {
              const parentPath = getParentPath(currentMove);
              return !parentPath ? '#333' : 'gold';
            } else {
              return currentMove === '-1' ? '#333' : 'gold';
            }
          })(),
          border: 'none',
          fontSize: '18px',
          cursor: (() => {
            if (isPuzzleNavigationDisabled) {
              return 'default'; // Default cursor for disabled puzzle mode
            } else if (isCommentMode) {
              return 'pointer'; // Always clickable in comment mode
            } else if (isInVariation) {
              const parentPath = getParentPath(currentMove);
              return !parentPath ? 'default' : 'pointer';
            } else {
              return currentMove === '-1' ? 'default' : 'pointer';
            }
          })(),
        }}
        title={isCommentMode && currentCommentMoveIndex === -1 ? "Exit comment mode" : (isInVariation ? "Go to parent move" : "Go to start position")}
      >
        <i className="fa-solid fa-backward-fast"></i>
      </button>
      
      {/* Previous Move Button - One move back in current variation/line */}
      <button
        onClick={() => {
          setIsPlaying(false);
          if (isCommentMode && onCommentMoveClick) {
            // Comment mode navigation
            if (currentCommentMoveIndex === -1) {
              // User is at START position trying to go back - directly delete comment state instead of showing confirmation
              // console.log('🚨 Previous button: At START position, auto-deleting comment state');
              onConfirmDelete && onConfirmDelete();
            } else {
              // Normal navigation within comment moves
              const prevIndex = Math.max(-1, currentCommentMoveIndex - 1);
              onCommentMoveClick(prevIndex);
            }
          } else {
            const previousPath = getPreviousMovePath(currentMove, moves);
            if (previousPath) {
              goToMove(previousPath);
            }
          }
        }}
        disabled={(() => {
          if (isPuzzleNavigationDisabled) {
            return true; // Disabled in puzzle mode until complete
          } else if (isCommentMode) {
            return false; // Always enabled in comment mode
          } else {
            const previousPath = getPreviousMovePath(currentMove, moves);
            return !previousPath;
          }
        })()}
        style={{
          padding: '14px 6px',
          backgroundColor: 'transparent',
          color: (() => {
            if (isPuzzleNavigationDisabled) {
              return '#333'; // Disabled color for puzzle mode
            } else if (isCommentMode) {
              // Red when at START position (boundary), gold otherwise
              return currentCommentMoveIndex === -1 ? '#ff4444' : 'gold';
            } else {
              const previousPath = getPreviousMovePath(currentMove, moves);
              return !previousPath ? '#333' : 'gold';
            }
          })(),
          border: 'none',
          fontSize: '18px',
          cursor: (() => {
            if (isPuzzleNavigationDisabled) {
              return 'default'; // Default cursor for disabled puzzle mode
            } else if (isCommentMode) {
              return 'pointer'; // Always clickable in comment mode
            } else {
              const previousPath = getPreviousMovePath(currentMove, moves);
              return !previousPath ? 'default' : 'pointer';
            }
          })()
        }}
        title={isCommentMode && currentCommentMoveIndex === -1 ? "Exit comment mode" : "Previous move in current line"}
      >
        <i className="fa-solid fa-angle-left"></i>
      </button>
      
      {/* Play/Pause Button in Center */}
      <button
        onClick={handlePlayClick}
        disabled={moves.length === 0}
        style={{
          padding: '14px 6px',
          backgroundColor: 'transparent',
          color: moves.length === 0 ? '#333' : (effectiveIsPlaying ? '#e91e63' : 'gold'),
          border: 'none',
          fontSize: '18px',
          cursor: moves.length === 0 ? 'default' : 'pointer'
        }}
        title={effectiveIsPlaying ? "Pause auto-play" : "Auto-play moves"}
      >
        <i className={effectiveIsPlaying ? "fa-solid fa-pause" : "fa-solid fa-play"}></i>
      </button>
      
      {/* Next Move Button - One move forward in current variation/line */}
      <button
        onClick={() => {
          setIsPlaying(false);
          if (isCommentMode && onCommentMoveClick) {
            // Comment mode - go to next comment move
            const nextIndex = Math.min(workingMoves.length - 1, currentMoveIndex + 1);
            if (nextIndex > currentMoveIndex) {
              onCommentMoveClick(nextIndex);
            }
          } else {
            const nextPath = getNextMovePath(currentMove, moves);
            if (nextPath) {
              goToMove(nextPath);
            }
          }
        }}
        disabled={(() => {
          if (isPuzzleNavigationDisabled) {
            return true; // Disabled in puzzle mode until complete
          } else if (isCommentMode) {
            return currentCommentMoveIndex >= workingMoves.length - 1;
          } else {
            const nextPath = getNextMovePath(currentMove, moves);
            return !nextPath;
          }
        })()}
        style={{
          padding: '14px 6px',
          backgroundColor: 'transparent',
          color: (() => {
            if (isPuzzleNavigationDisabled) {
              return '#333'; // Disabled color for puzzle mode
            } else if (isCommentMode) {
              return currentCommentMoveIndex >= workingMoves.length - 1 ? '#333' : 'gold';
            } else {
              const nextPath = getNextMovePath(currentMove, moves);
              return !nextPath ? '#333' : 'gold';
            }
          })(),
          border: 'none',
          fontSize: '18px',
          cursor: (() => {
            if (isPuzzleNavigationDisabled) {
              return 'default'; // Default cursor for disabled puzzle mode
            } else if (isCommentMode) {
              return currentCommentMoveIndex >= workingMoves.length - 1 ? 'default' : 'pointer';
            } else {
              const nextPath = getNextMovePath(currentMove, moves);
              return !nextPath ? 'default' : 'pointer';
            }
          })()
        }}
        title="Next move in current line"
      >
        <i className="fa-solid fa-angle-right"></i>
      </button>
      
      {/* Last Move Button - Jump to end of current variation or main line */}
      <button
        onClick={() => {
          setIsPlaying(false);
          if (isCommentMode && onCommentMoveClick) {
            // Comment mode - go to last comment move
            const lastIndex = workingMoves.length - 1;
            if (lastIndex >= 0) {
              onCommentMoveClick(lastIndex);
            }
          } else if (isInVariation) {
            const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
            if (endOfLine) {
              goToMove(endOfLine);
            }
          } else {
            // Main line - go to last move
            goToMove(String(moves.length - 1));
          }
        }}
        disabled={(() => {
          if (isPuzzleNavigationDisabled) {
            return true; // Disabled in puzzle mode until complete
          } else if (isCommentMode) {
            return currentCommentMoveIndex >= workingMoves.length - 1;
          } else if (isInVariation) {
            const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
            return !endOfLine;
          } else {
            return currentMoveIndex === moves.length - 1;
          }
        })()}
        style={{
          padding: '14px 6px',
          backgroundColor: 'transparent',
          color: (() => {
            if (isPuzzleNavigationDisabled) {
              return '#333'; // Disabled color for puzzle mode
            } else if (isCommentMode) {
              return currentCommentMoveIndex >= workingMoves.length - 1 ? '#333' : 'gold';
            } else if (isInVariation) {
              const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
              return !endOfLine ? '#333' : 'gold';
            } else {
              return currentMoveIndex === moves.length - 1 ? '#333' : 'gold';
            }
          })(),
          border: 'none',
          fontSize: '18px',
          cursor: (() => {
            if (isPuzzleNavigationDisabled) {
              return 'default'; // Default cursor for disabled puzzle mode
            } else if (isCommentMode) {
              return currentCommentMoveIndex >= workingMoves.length - 1 ? 'default' : 'pointer';
            } else if (isInVariation) {
              const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
              return !endOfLine ? 'default' : 'pointer';
            } else {
              return currentMoveIndex === moves.length - 1 ? 'default' : 'pointer';
            }
          })()
        }}
        title={isInVariation ? "Go to end of current variation" : "Go to end of game"}
      >
        <i className="fa-solid fa-forward-fast"></i>
      </button>
    </div>
  );
};

// Moves and Engine Content Component - Separate export for flexible positioning  
export const MobileMovesEngine = ({ 
  showStockfish = false,
  stockfishEvaluation = null,
  height = 120,
  comments = [],
  onCommentClick = null,
  hideTabSidebar = false,  // Add prop to hide sidebar in Record mode
  hideMainLineHeader = false,  // Add prop to hide Main Line Moves header in Record mode
  // Board communication props for Board Customization
  onActiveColorChange = null,
  onMobileArrowModeChange = null,
  onMobileArrowColorChange = null,
  onMobileArrowStartChange = null,
  // Board ref for direct visual updates
  boardRef = null,
  // Comment mode props
  isCommentMode = false,
  commentModeSource = null,
  commentStep = 1,
  commentMoves = [],
  currentCommentMoveIndex = -1,
  parentMoveIndex = null,  // Add stable parent move index from ViewGameMobile
  showConfirmation = false,
  onExitCommentMode = null,
  onGoBackToMoves = null,
  onProceedToCaption = null,
  onSubmitComment = null,
  onCommentMoveClick = null,
  onCommentMoveContentChange = null,
  onDeleteHalfMoves = null,
  onConfirmDelete = null,
  onCancelDelete = null,
  // Callback to update comment mode source when user manually switches tabs
  onUpdateCommentModeSource = null,
  // Puzzle mode props
  gameHasPuzzleTag = false,
  puzzleProgress = { current: 0, total: 0 },
  puzzleComplete = false,
  onPuzzleRetry = null,
  onPuzzleSolution = null,
  currentFEN = null,
  puzzleCurrentMove = null,
  onNextPuzzle = null,
  isInPuzzleFilter = false,
  redirectToPortal = null,
  // Function to open full comments page
  setShowComments = null,
  // Hide specific tabs when used on AddGame
  hideTabsOnAddGame = false,
  // Indicates if component is being used on AddGame page (affects default tab logic)
  isOnAddGame = false,
  // Indicates if game has lesson tag
  gameHasLessonTag = false,
  // Comment variation helper functions
  findCommentVariations = null,
  goToMoveCallback = null,
  // Comment submission handler for MobileCommentInput
  onCommentSubmit = null,
  // Current move notation for link display
  currentMoveNotation = null,
  // Comment move data props for content isolation
  commentMoveData = null,
  onUpdateCommentMoveContent = null
}) => {
  const history = useHistory();
  
  // Add state to track if we're in a puzzle filter from URL (copy from desktop GameSettingsPopup)
  const [isInPuzzleFilterFromUrl, setIsInPuzzleFilterFromUrl] = useState(false);

  // Check URL for puzzle filter when component mounts (copy from desktop GameSettingsPopup)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlTag = urlParams.get('tag');
    
    // Check for puzzle tag with case insensitivity (matches desktop GameSettingsPopup)
    if (urlTag && urlTag.toUpperCase() === 'PUZZLE') {
      setIsInPuzzleFilterFromUrl(true);
    }
  }, []);
  
  // Default tab logic:
  // - AddGame + Puzzle: stockfish (so creator can see solution)
  // - ViewGame + Puzzle: puzzle (so users can interact with puzzle)
  // - Lesson (both AddGame and ViewGame): moves (for unlimited moves & variations)
  // - All others: moves
  const getDefaultTab = () => {
    if (gameHasPuzzleTag) {
      return isOnAddGame ? 'moves' : 'puzzle';
    }
    if (gameHasLessonTag) {
      return 'moves';
    }
    return 'moves';
  };
  
  // Track previous tab and handle comment mode switching
  const [savedTabBeforeComment, setSavedTabBeforeComment] = useState(null);
  
  const [activeTab, setActiveTabInternal] = useState(getDefaultTab());
  
  // Wrapped setActiveTab to debug when tabs are being changed
  const setActiveTab = (newTab) => {
    // console.log('📱 [DEBUG] setActiveTab called:', { from: activeTab, to: newTab, isCommentMode, commentModeSource });
    setActiveTabInternal(newTab);
  };
  
  // Save current tab when entering comment mode (separate effect to avoid render loops)
  useLayoutEffect(() => {
    if (isCommentMode && !savedTabBeforeComment) {
      // console.log('💾 Saving current tab before comment mode:', activeTab);
      setSavedTabBeforeComment(activeTab);
    }
  }, [isCommentMode, activeTab, savedTabBeforeComment]);
  
  // Handle comment mode tab switching
  useLayoutEffect(() => {
    // console.log('🔍 [DEBUG] Comment mode useLayoutEffect triggered:', {
    //   isCommentMode,
    //   commentModeSource,
    //   activeTab,
    //   savedTabBeforeComment
    // });
    
    if (isCommentMode) {
      // Add defensive check for commentModeSource timing
      if (commentModeSource === null) {
        // console.log('⚠️ [WARNING] commentModeSource is null, waiting for proper value...');
        return; // Exit early and wait for commentModeSource to be set
      }
      
      // Only switch to comment tab if NOT initiated by Stockfish
      // This allows Stockfish moves to build comment variations in the background
      if (commentModeSource !== 'stockfish') {
        // console.log('🔄 NON-STOCKFISH: Switching to comment tab (source:', commentModeSource, ')');
        setActiveTab('custom');
      } else {
        // console.log('🤖 STOCKFISH: Staying on current tab:', activeTab, '- NOT switching to custom');
        // Do NOT call setActiveTab here - that's the key fix
      }
    } else {
      // Reset comment mode states when exiting
      // console.log('🏁 Exiting comment mode');
      setCommentCustomizationMode(false);
      setCommentCaption('');
      // Restore the saved tab when exiting comment mode
      if (savedTabBeforeComment) {
        // console.log('🔄 Restoring saved tab:', savedTabBeforeComment);
        setActiveTab(savedTabBeforeComment);
        setSavedTabBeforeComment(null); // Clear the saved tab
      }
    }
  }, [isCommentMode, commentModeSource]);
  
  // Sub-tab state for custom tab (ViewGame mode)
  const [customSubTab, setCustomSubTab] = useState('comments'); // 'comments' or 'annotations'
  
  // Track if user came from comments section to return them there when closing expanded comments
  const [cameFromComments, setCameFromComments] = useState(() => {
    // Check localStorage to see if user should return to comments
    return localStorage.getItem('returnToComments') === 'true';
  });
  
  // Static puzzle turn detection - determine once from starting position and never change
  const [staticPuzzleTurn, setStaticPuzzleTurn] = useState('White to move');
  const puzzleTurnSetRef = useRef(false); // Track if we've already set the turn
  
  // Set the static turn only once when puzzle first loads
  useEffect(() => {
    if (gameHasPuzzleTag && currentFEN && !puzzleTurnSetRef.current) {
      const fenParts = currentFEN.split(' ');
      // console.log('Setting static puzzle turn from starting FEN (ONCE ONLY):', currentFEN);
      if (fenParts.length >= 2) {
        const activeColor = fenParts[1];
        const turnText = activeColor === 'w' ? 'White to move' : 'Black to move';
        // console.log('Static puzzle turn set to:', turnText);
        setStaticPuzzleTurn(turnText);
        puzzleTurnSetRef.current = true; // Mark as set, never change again
      }
    }
  }, [gameHasPuzzleTag, currentFEN]);
  
  const getPuzzleTurnText = () => {
    return staticPuzzleTurn;
  };

  const getPuzzleTurnColor = () => {
    return staticPuzzleTurn.includes('White') ? 'white' : 'black';
  };
  
  
  // Handle returning from expanded comments - switch back to custom tab if user came from there
  useEffect(() => {
    if (cameFromComments) {
      // console.log('🔁 [INTERFERING?] cameFromComments effect triggered - switching to custom tab');
      setActiveTab('custom'); // Return to the collapsed comments tab
      setCameFromComments(false); // Reset the flag
      localStorage.removeItem('returnToComments'); // Clear localStorage
    }
  }, [cameFromComments]);
  
  // Check on component mount if we should return to comments
  useEffect(() => {
    const shouldReturnToComments = localStorage.getItem('returnToComments') === 'true';
    if (shouldReturnToComments) {
      // console.log('🔁 [INTERFERING?] localStorage returnToComments effect triggered - switching to custom tab');
      setActiveTab('custom');
      localStorage.removeItem('returnToComments');
      setCameFromComments(false);
    }
  }, []); // Only run on mount
  
  // Comment mode customization state
  const [commentCustomizationMode, setCommentCustomizationMode] = useState(false);
  const [commentCaption, setCommentCaption] = useState('');
  
  // State for expanded comment input in comments tab
  const [isCommentInputExpanded, setIsCommentInputExpanded] = useState(false);

  // Function to calculate move display format - uses parent-aware numbering like variations
  const formatCommentMove = (move, moveIndex, parentMoveIndex) => {
    // Use the exact same logic as variations (lines 443-446)
    // Continue numbering from parent move using cumulative calculation
    const totalMoveIndex = parentMoveIndex + 1 + moveIndex; // +1 because comments start from next move after parent
    const moveNumber = Math.floor(totalMoveIndex / 2) + 1;
    const isWhiteMove = totalMoveIndex % 2 === 0;
    
    // Follow standard chess notation:
    // - White moves always get numbers: "4.e4"
    // - Black moves only get numbers on FIRST move: "4...e5", then just "Nf6"
    if (isWhiteMove) {
      return `${moveNumber}.${move.notation}`;
    } else {
      // Only show number for first black move in the sequence
      if (moveIndex === 0) {
        return `${moveNumber}...${move.notation}`;
      } else {
        // Subsequent black moves don't need numbers
        return move.notation;
      }
    }
  };
  
  // Board customization tool states
  const [toolMode, setToolMode] = useState('TEXT'); // TEXT, SQUARE, ARROW, STAR (default: Annotations)
  const [selectedColor, setSelectedColor] = useState(constants.CHESSBOARD.COLOR_VARIANTS.RED);
  const [arrowStartSquare, setArrowStartSquare] = useState(null);
  const [annotationContent, setAnnotationContent] = useState('');
  
  // Game logic and state hooks (must come before useEffect)
  const { moves, currentMove, goToMove } = useGameLogic();
  const { state, dispatch } = useGameState();
  const movesContainerRef = useRef(null);
  const moveRefs = useRef(new Map());
  const variationMoveRefs = useRef(new Map());
  const commentMoveRefs = useRef(new Map());
  const commentMovesContainerRef = useRef(null);
  
  // Load current move's content when switching to TEXT tool or when move changes
  useEffect(() => {
    // console.log(`[ContentDebug] useEffect triggered:`, {
    //   toolMode,
    //   currentMove,
    //   currentMoveType: typeof currentMove,
    //   isCommentMode,
    //   hasCommentMoveData: !!commentMoveData,
    //   condition: toolMode === 'TEXT' && currentMove !== undefined
    // });
    
    if (toolMode === 'TEXT' && currentMove !== undefined) {
      let currentContent = '';
      
      // console.log(`[ContentDebug] Loading content for move:`, {
      //   currentMove,
      //   isCommentMode,
      //   toolMode,
      //   stateMovesLength: state?.moves?.length,
      //   commentMoveData: commentMoveData,
      //   trigger: 'useEffect'
      // });
      
      // Check if we're in comment mode and have comment move data
      if (isCommentMode && commentMoveData) {
        // COMMENT MODE: Load content from comment mode data
        currentContent = commentMoveData.content || '';
        // console.log(`[ContentDebug] Comment mode content loaded:`, {
        //   content: currentContent,
        //   commentMoveData: commentMoveData
        // });
      } else {
        // NORMAL MODE: Load content from game state
        if (currentMove === '-1') {
          currentContent = state.startPosition?.content || '';
          // console.log(`[ContentDebug] Start position content:`, currentContent);
        } else if (currentMove && !currentMove.includes('|')) {
          const moveIndex = parseInt(currentMove);
          const move = state.moves[moveIndex];
          currentContent = move?.content || '';
          console.log(`[ContentDebug] Main line move ${moveIndex} content:`, {
            content: currentContent,
            move: move,
            hasContent: !!move?.content
          });
        } else if (currentMove && currentMove.includes('|')) {
          // Handle variation moves using VariationPathUtils for proper nested support
          try {
            const result = getNodeAtPathInfinite(state.moves, currentMove, state);
            if (result.success && result.node) {
              currentContent = result.node.content || '';
              // console.log(`[ContentDebug] Variation move content:`, {
              //   currentMove,
              //   content: currentContent,
              //   node: result.node,
              //   hasContent: !!result.node.content
              // });
            } else {
              console.warn('Failed to get node content for path:', currentMove, result.error);
              currentContent = '';
            }
          } catch (error) {
            console.error('Error loading variation content:', error);
            currentContent = '';
          }
        }
      }
      // console.log(`[ContentDebug] Final content being set:`, currentContent);
      setAnnotationContent(currentContent);
    } else if (toolMode !== 'TEXT') {
      // Clear annotation content when not in TEXT mode to prevent state persistence issues
      setAnnotationContent('');
    }
  }, [toolMode, currentMove, state, isCommentMode, state?.moves, commentMoveData]);
  
  // Communicate tool states to board via props
  useEffect(() => {
    if (onActiveColorChange) {
      onActiveColorChange(toolMode === 'SQUARE' ? selectedColor : undefined);
    }
  }, [toolMode, selectedColor, onActiveColorChange]);
  
  // Reset tool mode when navigating away from custom tab (but preserve during comment customization)
  useEffect(() => {
    if (activeTab !== 'custom' && !commentCustomizationMode) {
      if (toolMode !== 'TEXT') {
        setToolMode('TEXT');
        setArrowStartSquare(null);
      }
    }
  }, [activeTab, toolMode, commentCustomizationMode]);
  
  useEffect(() => {
    if (onMobileArrowModeChange) {
      onMobileArrowModeChange(toolMode === 'ARROW');
    }
  }, [toolMode, onMobileArrowModeChange]);
  
  useEffect(() => {
    if (onMobileArrowColorChange) {
      onMobileArrowColorChange(toolMode === 'ARROW' ? selectedColor : undefined);
    }
  }, [toolMode, selectedColor, onMobileArrowColorChange]);
  
  useEffect(() => {
    if (onMobileArrowStartChange) {
      onMobileArrowStartChange(arrowStartSquare);
    }
  }, [arrowStartSquare, onMobileArrowStartChange]);
  
  // Cleanup tool mode on component unmount to ensure board state is reset
  useEffect(() => {
    return () => {
      // Reset all board customization states when component unmounts
      if (onMobileArrowModeChange) {
        onMobileArrowModeChange(false);
      }
      if (onActiveColorChange) {
        onActiveColorChange(undefined);
      }
      if (onMobileArrowColorChange) {
        onMobileArrowColorChange(undefined);
      }
      if (onMobileArrowStartChange) {
        onMobileArrowStartChange(null);
      }
    };
  }, [onMobileArrowModeChange, onActiveColorChange, onMobileArrowColorChange, onMobileArrowStartChange]);
  
  // Parse current move to get index
  const currentMoveIndex = currentMove === '-1' ? -1 : parseInt(currentMove) || 0;

  // Helper functions to check current move state
  const getCurrentMoveHasContent = () => {
    if (currentMove === '-1') {
      return state.startPosition?.content && state.startPosition.content.trim() !== '';
    } else if (!currentMove.includes('|')) {
      const moveIndex = parseInt(currentMove, 10);
      return state.moves[moveIndex]?.content && state.moves[moveIndex].content.trim() !== '';
    } else {
      // Handle variation moves using VariationPathUtils for proper nested support
      try {
        const result = getNodeAtPathInfinite(state.moves, currentMove, state);
        if (result.success && result.node) {
          return result.node.content && result.node.content.trim() !== '';
        } else {
          return false;
        }
      } catch (error) {
        console.error('Error checking variation content:', error);
        return false;
      }
    }
  };

  const getCurrentMoveIsStarred = () => {
    if (currentMove === '-1') return false;
    return state.starredMoves?.includes(currentMove) || false;
  };

  const getCurrentMoveNotation = () => {
    if (currentMove === '-1') return 'Start';
    if (currentMove && !currentMove.includes('|')) {
      const moveIndex = parseInt(currentMove, 10);
      return state.moves[moveIndex]?.notation || `Move ${moveIndex + 1}`;
    }
    return 'Current move';
  };

  // Function to scroll current move into view
  const scrollToCurrentMove = () => {
    const currentElement = moveRefs.current.get(currentMoveIndex);
    if (currentElement) {
      currentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  };

  // Scroll to current move within its variation container
  const scrollToCurrentVariationMove = () => {
    if (!currentMove || !currentMove.includes('|')) return;

    const variationKey = `variation-${currentMove}`;
    const moveElement = variationMoveRefs.current.get(variationKey);
    
    if (moveElement) {
      // The button is now a direct child of the scroll container
      // So we just scroll it into view directly
      moveElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  };

  // Watch for currentMove changes and scroll to keep it visible
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToCurrentMove();
      // Also scroll variation moves if we're in a variation
      if (currentMove && currentMove.includes('|')) {
        scrollToCurrentVariationMove();
      }
    }, 100); // Small delay to ensure DOM is updated
    
    return () => clearTimeout(timer);
  }, [currentMove]);

  // Watch for activeTab changes and scroll to current move when switching to 'moves' tab
  useEffect(() => {
    if (activeTab === 'moves') {
      const timer = setTimeout(() => {
        scrollToCurrentMove();
        // Also scroll variation moves if we're in a variation
        if (currentMove && currentMove.includes('|')) {
          scrollToCurrentVariationMove();
        }
      }, 100); // Small delay to ensure DOM is updated and tab content is rendered
      
      return () => clearTimeout(timer);
    }
  }, [activeTab]);

  // Function to scroll current comment move into view
  const scrollToCurrentCommentMove = () => {
    if (currentCommentMoveIndex !== undefined && currentCommentMoveIndex !== null) {
      const currentElement = commentMoveRefs.current.get(currentCommentMoveIndex);
      if (currentElement) {
        currentElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }
  };

  // Watch for comment move changes and scroll to keep current comment move visible
  useEffect(() => {
    if (isCommentMode && currentCommentMoveIndex !== undefined) {
      const timer = setTimeout(() => {
        scrollToCurrentCommentMove();
      }, 100); // Small delay to ensure DOM is updated
      
      return () => clearTimeout(timer);
    }
  }, [currentCommentMoveIndex, isCommentMode]);
  
  // Render move button with comprehensive styling - MobileMovesEngine version
  const renderMoveButton = (move, index, starredMoves = []) => {
    const isCurrentMove = index === currentMoveIndex;
    const moveNumber = Math.floor(index / 2) + 1;
    const isWhiteMove = index % 2 === 0;
    const moveHasVariations = hasVariations(move);
    
    const styling = getMoveButtonStyle(move, index, isCurrentMove, false, moveHasVariations, starredMoves, 'main');
    
    return (
      <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          ref={(el) => {
            if (el) {
              moveRefs.current.set(index, el);
            } else {
              moveRefs.current.delete(index);
            }
          }}
          onClick={() => goToMove(String(index))}
          style={{
            padding: '6px 10px',
            margin: '2px',
            backgroundColor: styling.backgroundColor,
            color: styling.color,
            border: styling.border || 'none',
            borderLeft: styling.borderLeft,
            borderBottom: styling.borderBottom,
            borderRadius: '4px',
            fontSize: '13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            minWidth: '60px',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
        >
          {/* Gold star before title for starred moves */}
          {styling.starred && (
            <i 
              className="fa-solid fa-star" 
              style={{
                color: '#ffd700',
                fontSize: '12px',
                marginRight: '4px',
                verticalAlign: 'top'
              }}
            />
          )}
          {isWhiteMove && <span style={{ color: isCurrentMove ? 'black' : '#888', marginRight: '4px' }}>{moveNumber}.</span>}
          {move.notation}
        </button>
      </div>
    );
  };
  
  // Group moves into pairs for display
  const renderMovePairs = () => {
    const pairs = [];
    for (let i = 0; i < moves.length; i += 2) {
      pairs.push(
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {moves[i] && renderMoveButton(moves[i], i, state?.starredMoves)}
          {moves[i + 1] && renderMoveButton(moves[i + 1], i + 1, state?.starredMoves)}
        </div>
      );
    }
    return pairs;
  };


  // Comment mode interface - conditionally override normal interface
  // Only show Comment Creation Interface if NOT triggered by Stockfish
  if (isCommentMode && commentModeSource !== 'stockfish') {
    // Confirmation subtab - completely overrides comment interface
    if (showConfirmation) {
      return (
        <div style={{
          width: '100%',
          maxWidth: '100%',
          height: `${Math.max(height, 150)}px`,
          backgroundColor: '',
          borderRadius: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          padding: '20px',
          gap: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'rgb(255, 68, 68)',
            textAlign: 'center'
          }}>
            Delete line?
          </div>
          
          <div style={{
            display: 'flex',
            gap: '16px'
          }}>
            <button
              onClick={() => onCancelDelete && onCancelDelete()}
              style={{
                padding: '12px 24px',
                backgroundColor: '#666',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Keep Editing
            </button>
            
            <button
              onClick={() => onConfirmDelete && onConfirmDelete()}
              style={{
                padding: '12px 24px',
                backgroundColor: 'rgb(255, 68, 68)',
                border: 'none',
                borderRadius: '8px',
                color: 'black',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              Delete
            </button>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        width: '100%',
        maxWidth: '100%',
        height: `${Math.max(height, 150)}px`,
        backgroundColor: 'transparent',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: '8px',
        boxSizing: 'border-box'
      }}>
        {/* Comment mode header with title and controls */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          paddingBottom: '5px',
        }}>
          {/* Left: Red X to exit or go back */}
          <button
            onClick={() => {
              if (commentCustomizationMode) {
                // In customization mode, return to moves
                setCommentCustomizationMode(false);
              } else if (commentStep === 2) {
                // In caption step, go back to moves
                onGoBackToMoves && onGoBackToMoves();
              } else {
                // In moves step, try to exit
                onExitCommentMode && onExitCommentMode();
              }
            }}
            style={{
              width: '32px',
              height: '32px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#ff4444',
              fontSize: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {(commentCustomizationMode || commentStep === 2) ? 
              <i className="fa-solid fa-arrow-left"></i> : 
              <i className="fa-solid fa-xmark"></i>
            }
          </button>
          
          {/* Center: Dynamic title in gold - positioned absolutely */}
          <div style={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#ffd700',
            textAlign: 'center'
          }}>
            {commentCustomizationMode ? 
              (() => {
                const moveText = commentMoves.length > 0 ? formatCommentMove(commentMoves[commentMoves.length - 1], commentMoves.length - 1, 0) : 'Move';
                switch(toolMode) {
                  case 'TEXT':
                    return `Annotate ${moveText}`;
                  case 'SQUARE':
                    return 'Highlight Squares';
                  case 'ARROW':
                    return 'Draw Arrows';
                  default:
                    return `Annotate ${moveText}`;
                }
              })() :
              commentStep === 2 ? 'Submit Comment' : 'New Variation'
            }
            
          </div>
          
          {/* Right: fa-comment button to navigate to captioning tab - only show in customization mode */}
          {commentCustomizationMode ? (
            <button
              onClick={() => {
                // Navigate to captioning tab (commentStep 2)
                setCommentCustomizationMode(false);
                onProceedToCaption && onProceedToCaption();
              }}
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#58b047',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <i className="fa-solid fa-comment"></i>
            </button>
          ) : (
            <div style={{ width: '32px' }}></div>
          )}
        </div>
        
        {/* Comment mode content */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {commentCustomizationMode ? (
            // Customization mode: Using AddGame styling
            <div style={{
              backgroundColor: 'transparent',
              borderRadius: '0px',
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box'
            }}>
              {/* Tool System Container with Background */}
              <div style={{
                backgroundColor: '#1a1a1a',
                borderRadius: '6px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}>
                {/* Row 2: Tool Selection Row */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  padding: '8px 4px',
                  gap: '4px',
                  borderBottom: '1px solid #333'
                }}>
                  {/* Annotate Tab */}
                  {/* Annotate Tool */}
                  <button
                    onClick={() => setToolMode('TEXT')}
                    style={{
                      backgroundColor: toolMode === 'TEXT' ? '#e91e63' : 'transparent',
                      border: 'none',
                      color: toolMode === 'TEXT' ? '#000' : '#888',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    title="Add Text Annotation"
                  >
                    <i className="fa-solid fa-font"></i>
                  </button>

                  {/* Square Tool */}
                  <button
                    onClick={() => setToolMode('SQUARE')}
                    style={{
                      backgroundColor: toolMode === 'SQUARE' ? '#e91e63' : 'transparent',
                      border: 'none',
                      color: toolMode === 'SQUARE' ? '#000' : (currentMoveHasSquares(currentMove, state) ? '#e91e63' : '#888'),
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    title="Highlight Squares"
                  >
                    <i className="fa-solid fa-square"></i>
                  </button>

                  {/* Arrow Tool */}
                  <button
                    onClick={() => setToolMode('ARROW')}
                    style={{
                      backgroundColor: toolMode === 'ARROW' ? '#e91e63' : 'transparent',
                      border: 'none',
                      color: toolMode === 'ARROW' ? '#000' : (currentMoveHasArrows(currentMove, state) ? '#e91e63' : '#888'),
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '3px 6px',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease'
                    }}
                    title="Draw Arrows"
                  >
                    <i className="fa-solid fa-arrow-up"></i>
                  </button>
                </div>

                {/* Row 3: Action Row */}
                <div style={{ flex: 1, minHeight: '40px', padding: '8px' }}>
                  {toolMode === 'STAR' && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      height: '100%'
                    }}>
                      <div style={{
                        color: '#ffd700',
                        fontSize: '14px',
                        textAlign: 'center',
                        fontWeight: 'bold'
                      }}>
                        {getCurrentMoveIsStarred() ? 'Move is starred' : 'Click to star'}
                      </div>
                    </div>
                  )}
                  
                  {toolMode === 'SQUARE' && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '8px'
                    }}>
                      {Object.entries(constants.CHESSBOARD.COLOR_VARIANTS).map(([name, color]) => (
                        <button
                          key={name}
                          onClick={() => setSelectedColor(color)}
                          style={{
                            backgroundColor: color,
                            border: selectedColor === color ? '2px solid white' : '1px solid #666',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            cursor: 'pointer',
                            transition: 'border 0.2s ease'
                          }}
                          title={name}
                        />
                      ))}
                      <button
                        onClick={() => {
                          // Clear board visual state immediately
                          if (boardRef?.current?.clearAllSquares) {
                            boardRef.current.clearAllSquares();
                          }
                          
                          // Clear all highlighted squares in Redux state
                          if (currentMove === '-1') {
                            dispatch({
                              type: 'UPDATE_START_POSITION_VISUALS',
                              payload: {
                                ...state.startPositionVisuals,
                                squares: []
                              }
                            });
                          } else if (currentMove && !currentMove.includes('|')) {
                            const moveIndex = parseInt(currentMove);
                            dispatch({
                              type: 'UPDATE_MOVE_VISUALS',
                              payload: { 
                                moveIndex,
                                visuals: { 
                                  ...state.moves[moveIndex]?.visuals,
                                  squares: [] 
                                } 
                              }
                            });
                          } else if (currentMove && currentMove.includes('|')) {
                            // Handle variation visuals clearing
                            const parts = currentMove.split('|');
                            const parentMoveIdx = parseInt(parts[0]);
                            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                            
                            dispatch({
                              type: 'UPDATE_VARIATION_VISUALS',
                              payload: {
                                parentMoveIndex: parentMoveIdx,
                                variationIndex: varIdx,
                                moveIndex: moveIdx,
                                visuals: {
                                  ...state.moves[parentMoveIdx]?.variations?.[varIdx]?.[moveIdx]?.visuals,
                                  squares: []
                                }
                              }
                            });
                          }
                        }}
                        style={{
                          backgroundColor: '#d64545',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                        title="Clear Squares"
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  {toolMode === 'ARROW' && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      marginTop: '8px'
                    }}>
                      {Object.entries(constants.CHESSBOARD.COLOR_VARIANTS).map(([name, color]) => (
                        <button
                          key={name}
                          onClick={() => setSelectedColor(color)}
                          style={{
                            backgroundColor: color,
                            border: selectedColor === color ? '2px solid white' : '1px solid #666',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            cursor: 'pointer',
                            transition: 'border 0.2s ease'
                          }}
                          title={name}
                        />
                      ))}
                      <button
                        onClick={() => {
                          // Clear board visual state immediately
                          if (boardRef?.current?.clearAllArrows) {
                            boardRef.current.clearAllArrows();
                          }
                          
                          // Clear all arrows and reset arrow start
                          onMobileArrowStartChange?.(null);
                          if (currentMove === '-1') {
                            dispatch({
                              type: 'UPDATE_START_POSITION_VISUALS',
                              payload: {
                                ...state.startPositionVisuals,
                                arrows: []
                              }
                            });
                          } else if (currentMove && !currentMove.includes('|')) {
                            const moveIndex = parseInt(currentMove);
                            dispatch({
                              type: 'UPDATE_MOVE_VISUALS',
                              payload: { 
                                moveIndex,
                                visuals: { 
                                  ...state.moves[moveIndex]?.visuals,
                                  arrows: [] 
                                } 
                              }
                            });
                          } else if (currentMove && currentMove.includes('|')) {
                            // Handle variation visuals clearing
                            const parts = currentMove.split('|');
                            const parentMoveIdx = parseInt(parts[0]);
                            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                            
                            dispatch({
                              type: 'UPDATE_VARIATION_VISUALS',
                              payload: {
                                parentMoveIndex: parentMoveIdx,
                                variationIndex: varIdx,
                                moveIndex: moveIdx,
                                visuals: {
                                  ...state.moves[parentMoveIdx]?.variations?.[varIdx]?.[moveIdx]?.visuals,
                                  arrows: []
                                }
                              }
                            });
                          }
                        }}
                        style={{
                          backgroundColor: '#d64545',
                          border: 'none',
                          borderRadius: '4px',
                          color: 'white',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                        title="Clear Arrows"
                      >
                        Clear
                      </button>
                    </div>
                  )}

                  {toolMode === 'TEXT' && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      height: '100%',
                      width: '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      marginTop: '5px',
                    }}>
                      <input
                        key={`annotation-${currentMove}-${isCommentMode}`}
                        type="text"
                        value={annotationContent}
                        onChange={(e) => {
                          const filteredValue = cleanProfanity(e.target.value);
                          setAnnotationContent(filteredValue);
                          
                          // In comment mode, also update the comment move data
                          if (isCommentMode && onUpdateCommentMoveContent) {
                            onUpdateCommentMoveContent('content', filteredValue);
                          }
                        }}
                        onBlur={() => {
                          // Save annotation content when user finishes editing
                          if (isCommentMode) {
                            // In comment mode, save directly to move properties (same as arrows)
                            // console.log(`[CommentBug] MobileMoves onBlur: Saving annotation content via dispatch:`, {
                            //   currentMove,
                            //   annotationContent,
                            //   isCommentMode
                            // });
                            
                            // Use same dispatch pattern as arrows to save content to move object
                            if (currentMove.startsWith('-1|')) {
                              // Start position variation - use specialized action (same as arrows)
                              dispatch({ 
                                type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
                                payload: { 
                                  moveId: currentMove,
                                  properties: { content: annotationContent }
                                }
                              });
                            } else {
                              // Regular move - use standard action (same as arrows)
                              dispatch({ 
                                type: 'UPDATE_MOVE_PROPERTIES', 
                                payload: { 
                                  moveId: currentMove,
                                  properties: { content: annotationContent }
                                }
                              });
                            }
                          } else {
                            // Normal mode - save to game state
                            if (currentMove === '-1') {
                              dispatch({
                                type: 'UPDATE_START_POSITION_CONTENT',
                                payload: annotationContent
                              });
                            } else if (currentMove && !currentMove.includes('|')) {
                              const moveIndex = parseInt(currentMove);
                              dispatch({
                                type: 'UPDATE_MOVE_CONTENT',
                                payload: { moveIndex, content: annotationContent }
                              });
                            } else if (currentMove && currentMove.includes('|')) {
                              // Handle variation content updates
                              const parts = currentMove.split('|');
                              
                              if (parts[0] === '-1') {
                                // Start position variation: -1|varIdx:moveIdx
                                const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                                dispatch({
                                  type: 'UPDATE_START_POSITION_VARIATION_MOVE_CONTENT',
                                  payload: {
                                    variationIdx: varIdx,
                                    moveIdx: moveIdx,
                                    content: annotationContent
                                  }
                                });
                              } else if (parts.length === 2) {
                                // First-level variation: mainMoveIdx|varIdx:moveIdx
                                const mainMoveIdx = parseInt(parts[0]);
                                const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                                dispatch({
                                  type: 'UPDATE_MOVE_VARIATION_CONTENT',
                                  payload: {
                                    mainMoveIdx: mainMoveIdx,
                                    variationIdx: varIdx,
                                    moveIdx: moveIdx,
                                    content: annotationContent
                                  }
                                });
                              } else {
                                // Nested variation (2+ levels): use new action that handles nested paths correctly
                                dispatch({
                                  type: 'UPDATE_NESTED_VARIATION_CONTENT',
                                  payload: {
                                    path: currentMove,
                                    content: annotationContent
                                  }
                                });
                              }
                            }
                          }
                        }}
                        placeholder="Enter annotation..."
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          backgroundColor: '#1a1a1a',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          color: '#ccc',
                          padding: '4px 6px',
                          fontSize: '13px',
                          outline: 'none',
                          height: '28px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : commentStep === 1 ? (
            // Step 1: Show moves being created
            <div 
              data-scrollable="true"
              style={{
              padding: '4px',
              paddingTop: '6px',
              backgroundColor: 'transparent',
              borderRadius: '5px',
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              position: 'relative'
            }}>
              
              {/* Render comment moves as buttons */}
              <div 
                ref={commentMovesContainerRef}
                className="mobile-moves-scrollbar"
                style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: '4px',
                padding: '2px 0px',
                borderRadius: '4px',
                minHeight: '35px',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollBehavior: 'smooth',
                width: '100%',
                maxWidth: '100%'
              }}>
                {commentMoves.length > 0 ? (
                  <>
                    {/* Starting position button */}
                    <button
                      ref={(el) => {
                        if (el) {
                          commentMoveRefs.current.set(-1, el);
                        } else {
                          commentMoveRefs.current.delete(-1);
                        }
                      }}
                      onClick={() => onCommentMoveClick && onCommentMoveClick(-1)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: currentCommentMoveIndex === -1 ? 'rgb(255, 68, 68)' : 'transparent',
                        border: 'none',
                        borderRadius: '4px',
                        color: '#000',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: currentCommentMoveIndex === -1 ? 1 : 0.7
                      }}
                    >
<i 
                        className="fa-solid fa-code-branch fa-rotate-90" 
                        style={{ 
                          fontSize: '18px', 
                          color: currentCommentMoveIndex === -1 ? 'rgb(10, 10, 10)' : 'rgb(255, 68, 68)' // Pink color
                          
                        }}
                      ></i>
                    </button>
                    
                    {/* Move buttons */}
                    {(() => {
                      // Use the stable parentMoveIndex passed from ViewGameMobile
                      // Convert it to cumulative index if needed
                      const stableParentMoveIndex = (() => {
                        if (parentMoveIndex === null || parentMoveIndex === undefined) {
                          // Fallback to -1 if not provided
                          return -1;
                        }
                        if (parentMoveIndex === '-1' || parentMoveIndex === -1) {
                          return -1;
                        }
                        // Convert string parentMoveIndex to cumulative index
                        return calculateCumulativeMoveIndex(String(parentMoveIndex));
                      })();
                      
                      return commentMoves.map((move, index) => {
                        const isCurrentMove = index === currentCommentMoveIndex;
                        const isHalfMove = move.state === 'half';
                        const displayText = formatCommentMove(move, index, stableParentMoveIndex);
                      
                      // Use main line move button styling
                      const styling = getMoveButtonStyle(move, index, isCurrentMove, false, false, [], 'comment');
                      
                      return (
                        <button
                          key={move.moveId}
                          ref={(el) => {
                            if (el) {
                              commentMoveRefs.current.set(index, el);
                            } else {
                              commentMoveRefs.current.delete(index);
                            }
                          }}
                          onClick={() => onCommentMoveClick && onCommentMoveClick(index)}
                          style={{
                            padding: '6px 10px',
                            margin: '2px',
                            backgroundColor: styling.backgroundColor,
                            color: isHalfMove ? 'rgb(255, 68, 68)' : styling.color,
                            border: styling.border || 'none',
                            borderLeft: styling.borderLeft,
                            borderBottom: styling.borderBottom,
                            borderRight: styling.borderRight,
                            borderTop: styling.borderTop,
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            // Half-move opacity and fa-trash styling
                            opacity: isHalfMove ? 0.8 : 1,
                            filter: isHalfMove ? 'grayscale(30%)' : 'none',
                            position: 'relative'
                          }}
                          title={isHalfMove ? `Half-move: ${displayText} (will be deleted if overwritten)` : displayText}
                        >
                          {displayText}
                        </button>
                      );
                      });
                    })()}
                  </>
                ) : (
                  <div style={{ color: '#888', fontSize: '12px', fontStyle: 'italic' }}>
                    Make moves on the board to build your line...
                  </div>
                )}
              </div>

              {/* Floating Delete Half-moves button - positioned above the scrollable container */}
              {commentMoves.some(move => move.state === 'half') && (
                <button
                  onClick={() => {
                    // If at START position, auto-delete entire comment state like Delete & Exit
                    if (currentCommentMoveIndex === -1) {
                      // console.log('🚨 fa-trash: At START position, auto-deleting comment state');
                      onConfirmDelete && onConfirmDelete();
                    } else {
                      // Normal half-moves deletion
                      onDeleteHalfMoves && onDeleteHalfMoves();
                    }
                  }}
                  style={{
                    position: 'absolute',
                    top: '6px',
                    right: '0px',
                    width: '32px',
                    height: '32px',
                    padding: '0',
                    backgroundColor: 'rgb(10, 10, 10)',
                    color: 'rgb(255, 68, 68)',
                    border: 'none',
                    fontSize: '18px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    zIndex: 10,
                    opacity: '0.8'
                  }}
                  title="Delete all half-moves"
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              )}

              {/* Move Annotation Textarea - Only show for comment moves when a move is selected */}
              {/* {commentMoves.length > 0 && currentCommentMoveIndex >= 0 && currentCommentMoveIndex < commentMoves.length && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  marginBottom: '8px',
                  padding: '8px',
                  backgroundColor: '#222',
                  borderRadius: '4px',
                  border: '2px solid #58b047'
                }}>
                  <label style={{
                    color: '#58b047',
                    fontSize: '14px',
                    marginBottom: '5px',
                    fontWeight: 'bold'
                  }}>
                    Annotate Move: {formatCommentMove(commentMoves[currentCommentMoveIndex], currentCommentMoveIndex, 0)}
                  </label>
                  <textarea
                    value={commentMoves[currentCommentMoveIndex]?.content || ''}
                    onChange={(e) => {
                      if (onCommentMoveContentChange) {
                        onCommentMoveContentChange(currentCommentMoveIndex, e.target.value);
                      }
                    }}
                    placeholder="Add annotations for this move..."
                    maxLength="1000"
                    style={{
                      height: '60px',
                      padding: '8px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      background: '#333',
                      color: 'white',
                      border: '1px solid #666',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '60px',
                      maxHeight: '120px',
                      transition: 'border-color 0.2s ease',
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#58b047'}
                    onBlur={(e) => e.target.style.borderColor = '#666'}
                  />
                </div>
              )} */}
              
              {/* Bottom button container - only show if we have moves */}
              {commentMoves.length > 0 && (
                <div style={{
                  marginTop: '',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  {/* Board button for customization */}
                  {/* <button
                    onClick={() => setCommentCustomizationMode(!commentCustomizationMode)}
                    style={{
                      padding: '8px',
                      backgroundColor: commentCustomizationMode ? '#58b047' : 'gold',
                      border: 'none',
                      borderRadius: '5px',
                      color: commentCustomizationMode ? 'black' : 'black',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: '4px',
                      minWidth: '75px',
                    }}
                  > <i className="fa-solid fa-font"></i>
                 
                   
                  </button> */}
                  
                  {/* Add Caption button */}
                  <button
                    onClick={() => onProceedToCaption && onProceedToCaption()}
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      backgroundColor: '#58b047',
                      border: 'none',
                      borderRadius: '5px',
                      color: 'black',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '5px'
                    }}
                  > 
                  
                  Share<i className="fa fa-comment"></i>
                    
                   
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Step 2: Caption entry
            <div style={{
              padding: '8px',
              backgroundColor: 'transparent',
              borderRadius: '5px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              marginTop: '10px'
            }}>
              <UnifiedMobileCommentInput
                onSubmit={(data) => onSubmitComment && onSubmitComment(data.content)}
                placeholder="Caption your variation..."
                disabled={false}
                submitting={false}
                showLinkToMove={false}
                style={{
                  padding: '0px',
                  gap: '8px'
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      height: `${Math.max(height, 150)}px`,
      backgroundColor: 'transparent',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden',
      borderRadius: '10px'
    }}>
      {/* Left Tab Buttons Column - Reordered: fa-knight, fa-comments/arrow, fa-robot, fa-gear */}
      {!hideTabSidebar && (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundColor: 'transparent',
        padding: '8px 0px',
        // gap: '4px', Used with the settions icon, without any space-between
        width: '54px',
        alignItems: 'center'
      }}>
        {/* First Tab: fa-knight (Moves) */}
        <button
          onClick={() => {
            // If in comment mode triggered by Stockfish, switching to moves tab should show Comment Creation Interface
            if (isCommentMode && commentModeSource === 'stockfish') {
              // console.log('Moves tab clicked while in Stockfish comment mode - showing Comment Creation Interface');
              // Update the comment mode source to indicate user action, which will trigger Comment Creation Interface
              if (onUpdateCommentModeSource) {
                onUpdateCommentModeSource('user');
              }
            }
            setActiveTab('moves');
          }}
          style={{
            width: '32px',
            height: '32px',
            padding: '8px',
            backgroundColor: activeTab === 'moves' ? '#58b047' : 'transparent',
            color: activeTab === 'moves' ? '#1a1a1a' : '#666',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Moves"
        >
          <i className="fa-solid fa-chess-knight"></i>
        </button>


        {/* Second Tab: fa-comments/arrow - Comments for ViewGame, Arrows for AddGame */}
        <button
          onClick={() => {
            // If in comment mode triggered by Stockfish, switching to custom tab should show Comment Creation Interface
            if (isCommentMode && commentModeSource === 'stockfish') {
              // console.log('Custom tab clicked while in Stockfish comment mode - showing Comment Creation Interface');
              // Update the comment mode source to indicate user action, which will trigger Comment Creation Interface
              if (onUpdateCommentModeSource) {
                onUpdateCommentModeSource('user');
              }
            }
            setActiveTab('custom');
          }}
          style={{
            width: '32px',
            height: '32px',
            padding: '8px',
            backgroundColor: activeTab === 'custom' ? (window.location.pathname.includes('/games/') ? '#ff6600' : '#e91e63') : 'transparent',
            color: activeTab === 'custom' ? '#1a1a1a' : '#666',
            border: 'none',
            fontSize: '18px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
          title={window.location.pathname.includes('/games/') ? 'Comments' : 'Arrows'}
        >
          <i className={window.location.pathname.includes('/games/') ? 'fa-solid fa-comment' : 'fa-solid fa-font'}></i>
          {/* Orange notification badge for comments count in ViewGame */}
          {window.location.pathname.includes('/games/') && comments && comments.length > 0 && (
            <span style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              backgroundColor: '#FF0000',
              color: 'white',
              borderRadius: '50%',
              width: '14px',
              height: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '9px',
              fontWeight: 'bold'
            }}>
              {comments.length > 9 ? '9+' : comments.length}
            </span>
          )}
        </button>

        {/* Third Tab: fa-robot (Stockfish Engine) */}
        {showStockfish && (
          <button
            onClick={() => setActiveTab('stockfish')}
            style={{
              width: '32px',
              height: '32px',
              padding: '8px',
              backgroundColor: activeTab === 'stockfish' || (isCommentMode && commentModeSource === 'stockfish') ? '#ffd700' : 'transparent',
              color: activeTab === 'stockfish' || (isCommentMode && commentModeSource === 'stockfish') ? '#1a1a1a' : '#666',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Stockfish Engine"
          >
            <i className="fa-solid fa-robot"></i>
          </button>
        )}

        {/* Puzzle Tab: fa-puzzle-piece (conditionally rendered) - MOVED TO LAST POSITION */}
        {gameHasPuzzleTag && !hideTabsOnAddGame && (
          <button
            onClick={() => {
              // If in comment mode triggered by Stockfish, switching to puzzle tab should show Comment Creation Interface
              if (isCommentMode && commentModeSource === 'stockfish') {
                // console.log('Puzzle tab clicked while in Stockfish comment mode - showing Comment Creation Interface');
                // Update the comment mode source to indicate user action, which will trigger Comment Creation Interface
                if (onUpdateCommentModeSource) {
                  onUpdateCommentModeSource('user');
                }
              }
              setActiveTab('puzzle');
            }}
            style={{
              width: '32px',
            height: '32px',
              padding: '8px',
              backgroundColor: activeTab === 'puzzle' ? '#00f2ea' : 'transparent',
              color: activeTab === 'puzzle' ? '#1a1a1a' : '#666',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Puzzle Mode"
          >
            <i className="fa-solid fa-puzzle-piece"></i>
          </button>
        )}

        {/* Fourth Tab: fa-gear (Settings) */}
        {/* <button
          onClick={() => setActiveTab('settings')}
          style={{
            width: '42px',
            height: '42px',
            padding: '8px',
            backgroundColor: activeTab === 'settings' ? '#58b047' : 'transparent',
            color: activeTab === 'settings' ? '#000' : '#666',
            border: 'none',
            fontSize: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Settings"
        >
          <i className="fa-solid fa-gear"></i>
        </button> */}
      </div>
      )}
      
      {/* Content Area - Standardized Container */}
      <div style={{
        width: '100%',
        padding: '8px',
        // paddingBottom: activeTab === 'puzzle' ? '0px' : '8px',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        borderLeft: hideTabSidebar ? 'none' : '1px solid rgb(68, 68, 68)',
        minWidth: 0,
        maxWidth: hideTabSidebar ? '100vw' : 'calc(100vw - 70px)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        boxSizing: 'border-box'
      }}>
        {activeTab === 'moves' ? (() => {
          const variationLine = getCurrentVariationLine(currentMove, moves, state);
          
          if (variationLine) {
            // We're in a variation - show variation as main line
            const { variation, varIndex, parentMoveId, parentNotation, baseColor } = variationLine;
            
            return (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', maxWidth: 'calc(100vw - 60px)' }}>
                <div style={{ 
                  fontSize: '16px', 
                  color: '#1a1a1a', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                
                  gap: '8px',
                }}>
                  {currentMove.includes('|') ? (
                    // Variation mode: show breadcrumb navigation
                    <>
                      {/* <button
                        onClick={() => goToMove(parentMoveId)}
                        style={{
                          padding: '4px 6px',
                          backgroundColor: 'rgba(214, 69, 69, 0.9)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          flexShrink: 0,
                          minWidth: '20px',
                          height: '22px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }}
                        title={`Return to parent`}
                      >
                        ✕
                      </button> */}
                      <div style={{ 
                        maxWidth: '100%',
                        overflow: 'hidden'
                      }}>
                        {renderBreadcrumbs(currentMove, moves, state, goToMove)}
                      </div>
                    </>
                  ) : (
                    // Main line mode: show "Main Line Moves" (conditionally hidden in Record mode)
                    !hideMainLineHeader && (
                      <span>
                        Main Line Moves
                      </span>
                    )
                  )}
                </div>
                
               
                
                {/* Variation moves as main line */}
                <div 
                  ref={movesContainerRef}
                  className="mobile-moves-scrollbar"
                  style={{
                    ...MOVE_CONTAINER_CONSTANTS,
                    // paddingLeft: '5px',
                    paddingTop: '0px',
                    overflowY: 'hidden',
                    marginTop: (() => {
                      // Check if there are any nested variations that would be shown below
                      const nestedVariations = getCurrentMoveVariations(currentMove, moves, state);
                      const hasNestedVariations = nestedVariations && !nestedVariations.isSiblingVariation;
                      return hasNestedVariations ? '0px' : '15px';
                    })()
                  }}>
                  {variation.map((varMove, moveIndex) => {
                    const moveId = parentMoveId === '-1' ? 
                      `-1|${varIndex}:${moveIndex}` : 
                      `${parentMoveId}|${varIndex}:${moveIndex}`;
                    const isCurrentMove = currentMove === moveId;
                    
                    // Calculate correct move number continuing from parent position
                    let moveNumber;
                    let isWhiteMove;
                    
                    if (parentMoveId === '-1') {
                      // Start position variations begin from move 1
                      moveNumber = Math.floor(moveIndex / 2) + 1;
                      isWhiteMove = moveIndex % 2 === 0;
                    } else {
                      // Continue numbering from parent move using cumulative calculation
                      const parentMoveIndex = calculateCumulativeMoveIndex(parentMoveId, moves, state);
                      const totalMoveIndex = parentMoveIndex + 1 + moveIndex; // +1 because variation starts from next move
                      moveNumber = Math.floor(totalMoveIndex / 2) + 1;
                      isWhiteMove = totalMoveIndex % 2 === 0;
                    }
                    
                    // Determine if this variation has comment moves for styling
                    const hasCommentMoves = baseColor === 'rgba(255, 102, 0, 0.5)';
                    const styling = getMoveButtonStyle(varMove, moveIndex, isCurrentMove, true, false, state?.starredMoves, 'variation', hasCommentMoves);
                    
                    return (
                      <button
                        key={moveIndex}
                        ref={(el) => {
                          if (el) {
                            variationMoveRefs.current.set(`variation-${moveId}`, el);
                          } else {
                            variationMoveRefs.current.delete(`variation-${moveId}`);
                          }
                        }}
                        onClick={() => goToMove(moveId)}
                        style={{
                          ...styling,
                          position: 'relative',
                        }}
                      >
                        {/* Gold star for starred variation moves */}
                        {isStarred(moveId, state?.starredMoves) && (
                          <i 
                            className="fa-solid fa-star" 
                            style={{
                              color: '#ffd700',
                              fontSize: '12px',
                              marginRight: '4px',
                            }}
                          />
                        )}
                        {/* Move number inside button for white moves or first black move */}
                        {(isWhiteMove || (!isWhiteMove && moveIndex === 0)) && (
                          <span style={{ marginRight: '4px', opacity: 0.7 }}>
                            {isWhiteMove ? `${moveNumber}.` : `${moveNumber}...`}
                          </span>
                        )}
                        {varMove.notation}
                      </button>
                    );
                  })}
                </div>
                
                {/* Show variations from current variation position - only sub-variations, not siblings */}
                {(() => {
                  const variations = getCurrentMoveVariations(currentMove, moves, state);
                  // When in variation view, only show sub-variations, not sibling variations
                  if (variations && variations.isSiblingVariation) {
                    return null; // Don't render sibling variations in variation view mode
                  }
                  return renderVariationRow(variations, goToMove, currentMove, state, variationMoveRefs);
                })()}
              </div>
            );
          } else {
            // Normal main line mode
            return (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', maxWidth: 'calc(100vw - 60px)' }}>
                {!hideMainLineHeader && (
                  <div style={{ 
                    fontSize: '16px', 
                    color: '#1a1a1a', 
                    fontWeight: 'bold', 
                    backgroundColor: '#58b047',
                    padding: '3px 6px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '15px'
                  }}>
                    <span>
                      Main Line Moves
                    </span>
                  </div>
                )}
                <div 
                  ref={movesContainerRef}
                  className="mobile-moves-scrollbar"
                  style={{...MOVE_CONTAINER_CONSTANTS, marginBottom: '' }}>
                  {/* Start Position Button */}
                  <button
                    ref={(el) => {
                      if (el) {
                        moveRefs.current.set(-1, el);
                      } else {
                        moveRefs.current.delete(-1);
                      }
                    }}
                    onClick={() => goToMove('-1')}
                    style={{
                      padding: '6px 12px',
                      margin: '2px',
                      backgroundColor: currentMoveIndex === -1 ? '#58b047' : '#444',
                      color: currentMoveIndex === -1 ? 'black' : '#ccc',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '13px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    START
                  </button>
                  
                  {/* Moves */}
                  {moves.length > 0 ? (
                    <>
                      {renderMovePairs()}
                      
                      {/* END Button */}
                      <button
                        ref={(el) => {
                          if (el) {
                            moveRefs.current.set(moves.length, el);
                          } else {
                            moveRefs.current.delete(moves.length);
                          }
                        }}
                        onClick={() => goToMove(String(moves.length - 1))}
                        style={{
                          padding: '6px 12px',
                          margin: '2px',
                          backgroundColor: currentMoveIndex === moves.length - 1 ? '#d64545' : '#444',
                          color: currentMoveIndex === moves.length - 1 ? 'white' : '#ccc',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          fontWeight: 'bold',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <i class="fa-solid fa-flag-checkered fa-flip-horizontal"></i>
                      </button>
                    </>
                  ) : (
                    <div style={{
                      color: '#666',
                      fontSize: '13px',
                      padding: '10px',
                      fontStyle: 'italic'
                    }}>
                      No moves yet
                    </div>
                  )}
                </div>
                
                {/* Variation Row */}
                {renderVariationRow(getCurrentMoveVariations(currentMove, moves, state), goToMove, currentMove, state, variationMoveRefs)}
              </div>
            );
          }
        })() : activeTab === 'puzzle' ? (
          puzzleComplete ? (
            // Puzzle Completed - Show completion buttons
            <div style={{
              backgroundColor: 'transparent',
              borderRadius: '0px',
              height: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '20px',
              flex: 1,
              overflow: 'hidden'
            }}>
              {/* Completion Buttons */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center',
                width: '100%',
                maxWidth: '250px'
              }}>
                <button
                  onClick={onPuzzleRetry}
                  style={{
                    backgroundColor: '#ff6b6b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s ease',
                    width: '100%',
                  }}
                  onMouseDown={(e) => e.currentTarget.style.backgroundColor = '#ff5252'}
                  onMouseUp={(e) => e.currentTarget.style.backgroundColor = '#ff6b6b'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b6b'}
                >
                  <i className="fa-solid fa-redo"></i>
                  Restart Puzzle
                </button>
                
                <button
                  onClick={() => {
                    // Copy desktop handleNextPuzzle logic from GameSettingsPopup
                    // (no setPuzzleState or onClose needed in mobile)
                    
                    // If in puzzle filter and onNextPuzzle is provided, navigate to next puzzle
                    if ((isInPuzzleFilter || isInPuzzleFilterFromUrl) && onNextPuzzle) {
                      onNextPuzzle();
                    }
                    // If not in puzzle filter and redirectToPortal is provided, redirect to portal
                    else if (!(isInPuzzleFilter || isInPuzzleFilterFromUrl) && redirectToPortal) {
                      redirectToPortal();
                    }
                  }}
                  style={{
                    backgroundColor: '#58b047',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s ease',
                    width: '100%',
                  }}
                  onMouseDown={(e) => e.currentTarget.style.backgroundColor = '#4a9639'}
                  onMouseUp={(e) => e.currentTarget.style.backgroundColor = '#58b047'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#58b047'}
                >
                  <i className="fa-solid fa-puzzle-piece"></i>
                  {(isInPuzzleFilter || isInPuzzleFilterFromUrl) ? "Next Puzzle" : "Show More Puzzles"}
                </button>
              </div>
            </div>
          ) : (
            // Normal puzzle state - Show progress and controls
            <div style={{
              backgroundColor: 'transparent',
              borderRadius: '0px',
              height: '100%',
              width: '100%',
              // maxWidth: '280px',
              // margin: '0 auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              flex: 1,
              overflow: 'hidden'
            }}>

              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', backgroundColor: '#00f2ea',
                borderRadius: '6px', padding: '2px 6px', }}>
              {/* Puzzle Title - Standardized */}
              <div style={{ 
                fontSize: '16px', 
                color: '#1a1a1a', 
                fontWeight: 'bold',
                
                
                
                display: 'inline-block'
              }}>
                Puzzle
                
              </div>
              <div style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: getPuzzleTurnColor(),
                  border: getPuzzleTurnColor() === 'white' ? '2px solid #333' : '2px solid #666',
                  background: getPuzzleTurnColor() === 'white' ? '2px solid #333' : '2px solid #666',
                  borderRadius: '3px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  &nbsp;
                </div>
                </div>
              
           
                
                
                {/* Progress Display */}
                <div style={{
                  borderRadius: '8px',
                  background: '',
                  padding: '5px',
                }}>
                  <div style={{
                    color: '#58b047',
                    fontSize: '18px',
                    fontWeight: 'bold',
                   
                  }}>
                    Move {puzzleProgress.current} / {puzzleProgress.total}
                  </div>
                  
                  {/* Progress Bar */}
                  <div style={{
                    height: '10px',
                    backgroundColor: '#333',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginTop: '5px',
                    
                  }}>
                    <div style={{
                      width: `${puzzleProgress.total > 0 ? (puzzleProgress.current / puzzleProgress.total) * 100 : 0}%`,
                      height: '100%',
                      backgroundColor: '#58b047',
                      transition: 'width 0.3s ease'
                    }}></div>
                    
                  </div>
                 
              
                
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                justifyContent: 'center',
                padding: '0px 5px'
              }}>
                <button
                  onClick={onPuzzleRetry}
                  style={{
                    backgroundColor: '#ff6b6b',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    transition: 'background-color 0.2s ease',
                    flex: 1,
                  }}
                  onMouseDown={(e) => e.currentTarget.style.backgroundColor = '#ff5252'}
                  onMouseUp={(e) => e.currentTarget.style.backgroundColor = '#ff6b6b'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ff6b6b'}
                >
                  <i className="fa-solid fa-redo"></i>
                  Restart
                </button>
                
                <button
                  onClick={onPuzzleSolution}
                  style={{
                    backgroundColor: '#58b047',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '6px 16px',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    transition: 'background-color 0.2s ease',
                    flex: 1,
                  }}
                  onMouseDown={(e) => e.currentTarget.style.backgroundColor = '#4a9639'}
                  onMouseUp={(e) => e.currentTarget.style.backgroundColor = '#58b047'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#58b047'}
                >
                  <i className="fa-solid fa-lightbulb"></i>
                  Solution
                </button>
              </div>
            </div>
          )
        ) : activeTab === 'stockfish' || (isCommentMode && commentModeSource === 'stockfish') ? (
          <EngineTabContent 
            stockfishEvaluation={stockfishEvaluation}
          />
        ) : activeTab === 'custom' ? (
          (() => {
            // Detect if we're in AddGame vs ViewGame based on URL path
            const isViewGame = window.location.pathname.includes('/games/');
            const isAddGame = !isViewGame;
            
            if (isViewGame) {
              // ViewGame Tab with Sub-tabs: Comments and Annotations
              return (
                <div style={{
                  backgroundColor: 'transparent',
                  boxSizing: 'border-box',
                  height: '100%',
                  width: '100%',
                  maxWidth: 'calc(100vw - 60px)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                  }}>
                  {/* Conditionally render based on isCommentInputExpanded */}
                  {!isCommentInputExpanded ? (
                    <>
                      {/* Scrollable Comments List */}
                      <div 
                      data-scrollable="true"
                      style={{ 
                        overflowY: 'auto', 
                        flex: 1,
                        maxHeight: 'calc(100vh - 390px)', // Leave space for sticky button
                        minHeight: '150px', // Minimum height for usability
                        scrollBehavior: 'smooth',
                        // Custom scrollbar styling for mobile
                        WebkitOverflowScrolling: 'touch'
                      }}>
                      {/* Comment Previews - First 2 comments (larger) OR placeholder */}
                      {comments && comments.length > 0 ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      paddingBottom: '60px' // Space for sticky button
                    }}>
                      {/* "See all comments" container - only show when 3+ comments exist */}
                      {comments.length >= 0 && (
                        <div 
                          onClick={() => {
                            // Track that user came from comments section
                            setCameFromComments(true);
                            localStorage.setItem('returnToComments', 'true');
                            // Open full comments page directly
                            if (setShowComments) {
                              setShowComments(true);
                            }
                          }}
                          style={{
                            fontSize: '16px',
                            color: '#1a1a1a',
                            fontWeight: 'bold',
                            backgroundColor: '#ff6600',
                            padding: '2px',
                            paddingLeft: '6px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'opacity 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          Comments
                          <i className="fa-solid fa-arrow-right" style={{ fontSize: '18px' }}></i>
                        </div>
                      )}
                      {comments.slice(0, 2).map((comment, index) => {
                        const commentVariations = findCommentVariations ? findCommentVariations(comment.id, moves) : [];
                        const hasMovesPreview = commentVariations.length > 0;
                        
                        // Check if this comment is linked to current move
                        const isCurrentMoveComment = (comment.moveIndex === currentMove) || 
                          (hasMovesPreview && commentVariations.some(variation => 
                            variation.parentMoveId === currentMove || variation.variationMoveId === currentMove
                          ));
                        
                        return (
                          <div
                            key={comment.id || index}
                            style={{
                              fontSize: '16px',
                              padding: '8px 10px',
                              backgroundColor: isCurrentMoveComment ? 'rgba(255, 102, 0, 0.1)' : '#333',
                              borderRadius: '8px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              transition: 'background-color 0.2s ease',
                              border: 'none'
                            }}
                          >
                            {/* Top row: Username left | Moves right */}
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                            }}>
                              {/* **Username - Left** */}
                              <div 
                                onClick={() => {
                                  setCameFromComments(true);
                                  localStorage.setItem('returnToComments', 'true');
                                  if (setShowComments) {
                                    setShowComments(true);
                                  }
                                }}
                                style={{
                                  fontWeight: 'bold',
                                  color: 'rgba(255, 102, 0, 0.5)',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  flexShrink: 0
                                }}
                              >
                                @{comment.username}
                              </div>
                              
                              {/* Moves Preview - Right */}
                              {hasMovesPreview && (
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  gap: '3px',
                                  flexShrink: 0,
                                  alignItems: 'center'
                                }}>
                                  {commentVariations.slice(0, 2).map((variation, vIndex) => {
                                    // Calculate move number for parent move using cumulative calculation
                                    const parentCumulativeIndex = calculateCumulativeMoveIndex(variation.parentMoveId);
                                    const baseNumber = Math.floor(parentCumulativeIndex / 2) + 1;
                                    const isWhiteMove = parentCumulativeIndex % 2 === 0;
                                    const moveNum = isWhiteMove ? baseNumber : `${baseNumber}...`;
                                    
                                    return (
                                      <div key={vIndex} style={{
                                        display: 'flex',
                                        gap: '6px',
                                        alignItems: 'center'
                                      }}>
                                        {/* Parent Move Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (goToMoveCallback) {
                                              goToMoveCallback(variation.parentMoveId);
                                            }
                                          }}
                                          style={{
                                            backgroundColor: currentMove === variation.parentMoveId ? '#58b047' : '#666',
                                            color: currentMove === variation.parentMoveId ? 'black' : '#fff',
                                            border: 'none',
                                            borderRadius: '3px',
                                            padding: '4px 8px',
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            fontFamily: 'monospace',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'background-color 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#777'}
                                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#666'}
                                        >
                                          {moveNum}{moveNum.toString().includes('...') ? '' : '.'} {variation.parentMove.notation}
                                        </button>
                                        
                                        {/* Variation Branch Icon */}
                                        <i className="fa-solid fa-code-branch fa-rotate-90" style={{ color: '#ff6600', fontSize: '12px' }}></i>
                                        
                                        {/* THIS IS THE commentVariationMoveButton in comments tab with parent move + branch icon */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (goToMoveCallback) {
                                              goToMoveCallback(variation.variationMoveId);
                                            }
                                          }}
                                          style={{
                                            backgroundColor: currentMove === variation.variationMoveId ? '#58b047' : '#666',
                                            color: currentMove === variation.variationMoveId ? 'black' : '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            fontFamily: 'monospace',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'background-color 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#777'}
                                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#666'}
                                        >
                                          {variation.firstCommentMove.notation}
                                        </button>
                                      </div>
                                    );
                                  })}
                                  {commentVariations.length > 2 && (
                                    <span style={{
                                      color: '#888',
                                      fontSize: '10px',
                                      padding: '2px 4px',
                                      alignSelf: 'center'
                                    }}>
                                      +{commentVariations.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Move-linked comment navigation button - similar to desktop */}
                              {comment.moveIndex !== undefined && comment.moveIndex !== null && comment.moveNotation && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (goToMoveCallback) {
                                      goToMoveCallback(comment.moveIndex);
                                    }
                                  }}
                                  style={{
                                    background: currentMove === comment.moveIndex ? '#58b047' : '#666',
                                    color: currentMove === comment.moveIndex ? 'black' : 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    fontFamily: 'monospace',
                                    marginLeft: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    transition: 'background-color 0.2s ease',
                                    height: '18px',
                                    flexShrink: 0
                                  }}
                                  title={`Go to move: ${comment.moveNotation}`}
                                >
                                  {(() => {
                                    // Calculate move number display - same logic as desktop
                                    if (comment.moveIndex === '-1') {
                                      return 'Start Position';
                                    }
                                    const cumulativeIndex = calculateCumulativeMoveIndex(comment.moveIndex);
                                    const moveNumber = Math.floor(cumulativeIndex / 2) + 1;
                                    const isWhite = cumulativeIndex % 2 === 0;
                                    return `${moveNumber}${isWhite ? '.' : '...'} ${comment.moveNotation}`;
                                  })()}
                                </button>
                              )}
                            </div>
                            
                            {/* Bottom row: Content full width */}
                            <div 
                              onClick={() => {
                                setCameFromComments(true);
                                localStorage.setItem('returnToComments', 'true');
                                if (setShowComments) {
                                  setShowComments(true);
                                }
                              }}
                              style={{
                                color: '#ccc',
                                fontSize: '14px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                cursor: 'pointer',
                                lineHeight: '1.3',
                                width: '100%',
                                fontWeight: ''
                              }}
                            >
                              {cleanProfanity(comment.content)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '80px',
                      color: '#666',
                      fontSize: '16px',
                      textAlign: 'center',
                      fontStyle: 'italic',
                      marginBottom: '60px' // Space for sticky button
                    }}>
                      <span style={{
                        backgroundColor: '#1a1a1a',
                        color: 'rgba(255, 102, 0, 0.5)',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        // fontWeight: 'bold',
                        fontSize: '16px'
                      }}>
                        Be the first to add a comment
                      </span>
                    </div>
                  )}
                  
                  </div>
                      {/* Sticky Add Comment Button */}
                      <div style={{
                        position: 'sticky',
                        bottom: -10,
                        paddingBottom: '5px',
                        background: 'rgb(10, 10, 10)',
                        height: '100px'
                       
                      }}>
                        <button
                          onClick={() => setIsCommentInputExpanded(true)}
                          style={{
                            width: '100%',
                            height: '36px',
                            backgroundColor: '#ff6600',
                            color: '#1a1a1a',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                          }}
                        >
                          Add Comment
                          <i className="fa-solid fa-comment"></i>
                        </button>
                      </div>
                    </>
                  ) : (
                    /* Expanded Comment Input View - replaces the entire tab content */
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      width: '100%'
                    }}>

                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      {/* Header with back button */}
                      <button
                        onClick={() => setIsCommentInputExpanded(false)}
                        style={{
                          alignSelf: 'flex-start',
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#ff4444',
                          fontSize: '20px',
                          cursor: 'pointer',
                          padding: '8px',
                          // marginBottom: 'px'
                        }}
                      >
                        <i className="fa-solid fa-arrow-left"></i>
                        <span style={{ color: 'gold', fontWeight: 'bold', fontSize: '', marginLeft: '5px' }}> Add a Comment </span>
                      </button>
                      <button
                      onClick={() => {
                            // Track that user came from comments section
                            setCameFromComments(true);
                            localStorage.setItem('returnToComments', 'true');
                            // Open full comments page directly
                            if (setShowComments) {
                              setShowComments(true);
                            }
                          }}                      style={{
                        alignSelf: 'flex-start',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#ccc',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '8px',
                      }}
                      >
                        <i className="fa-solid fa-maximize"></i>

                      </button>


                      </div>




                      
                      {/* Full-size comment input centered in the tab */}
                      <div style={{ 
                        flex: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        maxWidth: '400px',
                        // margin: '0 auto',
                        marginTop: currentMove === '-1' ? '15px' : '0px'
                      }}>
                        <MobileCommentInput 
                          onSubmit={(commentData) => {
                            onCommentSubmit(commentData);
                            setIsCommentInputExpanded(false);
                          }}
                          currentMoveNotation={currentMoveNotation}
                          currentMove={currentMove} // Pass currentMove to hide link at start position
                          disabled={false}
                          submitting={false}
                        />
                      </div>
                    </div>
                  )}
                    </div>
                  ) : isCommentMode ? (
                    // Annotations Sub-tab - Only show in comment mode
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      padding: '12px'
                    }}>
                      <div style={{
                        fontSize: '16px',
                        color: '#58b047',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        Move Annotations
                      </div>

                      {/* Current Move Display */}
                      {(() => {
                        // Get current move content for display
                        let currentContent = '';
                        let moveDisplay = 'No move selected';
                        
                        if (currentMove === '-1') {
                          currentContent = state.startPosition?.content || '';
                          moveDisplay = 'Starting Position';
                        } else if (currentMove && !currentMove.includes('|')) {
                          const moveIndex = parseInt(currentMove);
                          currentContent = state.moves[moveIndex]?.content || '';
                          const moveObj = state.moves[moveIndex];
                          if (moveObj) {
                            const moveNumber = Math.floor(moveIndex / 2) + 1;
                            const isWhite = moveIndex % 2 === 0;
                            moveDisplay = `${moveNumber}${isWhite ? '.' : '...'} ${moveObj.notation}`;
                          }
                        } else if (currentMove && currentMove.includes('|')) {
                          const parts = currentMove.split('|');
                          
                          if (parts[0] === '-1') {
                            // Start position variation
                            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                            currentContent = state.startPosition?.variations?.[varIdx]?.[moveIdx]?.content || '';
                            const moveObj = state.startPosition?.variations?.[varIdx]?.[moveIdx];
                            if (moveObj) {
                              moveDisplay = `Starting Variation: ${moveObj.notation}`;
                            }
                          } else {
                            // Regular move variation
                            const mainMoveIdx = parseInt(parts[0]);
                            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                            currentContent = state.moves[mainMoveIdx]?.variations?.[varIdx]?.[moveIdx]?.content || '';
                            const moveObj = state.moves[mainMoveIdx]?.variations?.[varIdx]?.[moveIdx];
                            if (moveObj) {
                              const baseNumber = Math.floor(mainMoveIdx / 2) + 1;
                              moveDisplay = `${baseNumber}. ${moveObj.notation} (variation)`;
                            }
                          }
                        }

                        return (
                          <>
                            {/* Move Title */}
                            <div style={{
                              fontSize: '14px',
                              color: '#ffd700',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              padding: '8px',
                              backgroundColor: '#333',
                              borderRadius: '4px',
                              marginBottom: '8px'
                            }}>
                              {moveDisplay}
                            </div>

                            {/* Annotation Content */}
                            {currentContent ? (
                              <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                              }}>
                                <div style={{
                                  fontSize: '14px',
                                  color: '#58b047',
                                  fontWeight: 'bold'
                                }}>
                                  Annotation:
                                </div>
                                <textarea
                                  value={currentContent}
                                  onChange={(e) => {
                                    const newContent = cleanProfanity(e.target.value);
                                    // Save annotation content using the same logic as in the main annotation section
                                    if (currentMove === '-1') {
                                      // Start position content
                                      dispatch({
                                        type: 'UPDATE_START_POSITION_CONTENT',
                                        payload: { content: newContent }
                                      });
                                    } else if (currentMove && currentMove.includes('|')) {
                                      // Variation content
                                      const parts = currentMove.split('|');
                                      if (parts[0] === '-1') {
                                        // Start position variation
                                        const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                                        dispatch({
                                          type: 'UPDATE_START_POSITION_VARIATION_MOVE_CONTENT',
                                          payload: { variationIdx: varIdx, moveIdx: moveIdx, content: newContent }
                                        });
                                      } else {
                                        // Regular move variation
                                        const mainMoveIdx = parseInt(parts[0]);
                                        const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                                        dispatch({
                                          type: 'UPDATE_MOVE_VARIATION_CONTENT',
                                          payload: { mainMoveIdx, variationIdx: varIdx, moveIdx, content: newContent }
                                        });
                                      }
                                    } else if (currentMove && !currentMove.includes('|')) {
                                      // Main line move content
                                      const moveIndex = parseInt(currentMove);
                                      dispatch({
                                        type: 'UPDATE_MOVE_CONTENT',
                                        payload: { moveIndex, content: newContent }
                                      });
                                    }
                                  }}
                                  placeholder="Add annotations for this move..."
                                  style={{
                                    padding: '12px',
                                    backgroundColor: '#222',
                                    borderRadius: '6px',
                                    border: '2px solid #58b047',
                                    color: '#ccc',
                                    lineHeight: '1.4',
                                    minHeight: '80px',
                                    resize: 'vertical',
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    outline: 'none',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>
                            ) : (
                              <div style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '12px',
                                padding: '20px',
                                color: '#888',
                                textAlign: 'center'
                              }}>
                                <div style={{ fontSize: '16px' }}>
                                  No annotation for this move
                                </div>
                                <div style={{ fontSize: '14px', maxWidth: '250px' }}>
                                  Navigate to moves with annotations to view their explanations and strategic insights
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  ) : (
                    // Not in comment mode - show message that annotations are only available in comment mode
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '20px',
                      color: '#666',
                      textAlign: 'center'
                    }}>
                      <i className="fa-solid fa-info-circle" style={{ fontSize: '24px', color: '#58b047' }}></i>
                      <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        Annotations are only available in Comment Mode
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                        Create or view comments to access move annotations
                      </div>
                    </div>
                  )}
                </div>
              );
            } else {
              // AddGame Mode - Board Customization
              return (
                <div style={{
                  backgroundColor: 'transparent',
                  boxSizing: 'border-box',
                  height: '100%',
                  width: '100%',
                  maxWidth: 'calc(100vw - 60px)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  {/* Row 1: Title */}
                  <div style={{ 
                    fontSize: '16px', 
                    color: '#1a1a1a', 
                    fontWeight: 'bold', 
                    marginBottom: '0px',
                    backgroundColor: '#e91e63',
                    padding: '2px',
                    paddingLeft: '6px',
                    borderRadius: '6px',
                    display: 'inline-block'
                  }}>
                    {(() => {
                      const notation = getCurrentMoveNotation();
                      switch(toolMode) {
                        case 'TEXT':
                          return `Annotate ${notation}`;
                        case 'SQUARE':
                          return 'Highlight Squares';
                        case 'ARROW':
                          return 'Draw Arrows';
                        case 'STAR':
                          return 'Favorite Moves';
                        default:
                          return 'Customize Board';
                      }
                    })()} 
                  </div>

                  {/* Tool System Container with Background */}
                  <div style={{
                    backgroundColor: '#1a1a1a',
                    borderRadius: '6px',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden', 
                  }}>
                    {/* Row 2: Tool Icons */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-around',
                      alignItems: 'center',
                      padding: '10px 0',
                      borderBottom: '1px solid #333',
                      paddingBottom: '10px',
                      marginBottom: '5px'
                    }}>
                    {/* Annotate Tool - FIRST */}
                    <button
                      onClick={() => {
                        setToolMode('TEXT');
                        // Load current move's content
                        let currentContent = '';
                        if (currentMove === '-1') {
                          currentContent = state.startPosition?.content || '';
                        } else if (currentMove && !currentMove.includes('|')) {
                          const moveIndex = parseInt(currentMove);
                          currentContent = state.moves[moveIndex]?.content || '';
                        } else if (currentMove && currentMove.includes('|')) {
                          // Handle variation content loading
                          const parts = currentMove.split('|');
                          
                          if (parts[0] === '-1') {
                            // Start position variation: -1|varIdx:moveIdx
                            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                            currentContent = state.startPosition?.variations?.[varIdx]?.[moveIdx]?.content || '';
                          } else {
                            // Regular move variation: mainMoveIdx|varIdx:moveIdx
                            const mainMoveIdx = parseInt(parts[0]);
                            const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                            currentContent = state.moves[mainMoveIdx]?.variations?.[varIdx]?.[moveIdx]?.content || '';
                          }
                        }
                        setAnnotationContent(currentContent);
                      }}
                      style={{
                        backgroundColor: toolMode === 'TEXT' ? '#e91e63' : 'transparent',
                        border: 'none',
                        color: toolMode === 'TEXT' ? '#000' : (getCurrentMoveHasContent() ? '#58b047' : '#888'),
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      title="Add Text Annotation"
                    >
                      <i className="fa-solid fa-font"></i>
                    </button>

                    {/* Square Tool */}
                    <button
                      onClick={() => setToolMode('SQUARE')}
                      style={{
                        backgroundColor: toolMode === 'SQUARE' ? '#e91e63' : 'transparent',
                        border: 'none',
                        color: toolMode === 'SQUARE' ? '#000' : (currentMoveHasSquares(currentMove, state) ? '#e91e63' : '#888'),
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      title="Highlight Squares"
                    >
                      <i className="fa-solid fa-square"></i>
                    </button>

                    {/* Arrow Tool */}
                    <button
                      onClick={() => setToolMode('ARROW')}
                      style={{
                        backgroundColor: toolMode === 'ARROW' ? '#e91e63' : 'transparent',
                        border: 'none',
                        color: toolMode === 'ARROW' ? '#000' : (currentMoveHasArrows(currentMove, state) ? '#e91e63' : '#888'),
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      title="Draw Arrows"
                    >
                      <i className="fa-solid fa-arrow-up"></i>
                    </button>

                    {/* Star Tool - LAST */}
                    <button
                      onClick={() => setToolMode('STAR')}
                      style={{
                        backgroundColor: toolMode === 'STAR' ? '#e91e63' : 'transparent',
                        border: 'none',
                        color: toolMode === 'STAR' ? '#000' : (getCurrentMoveIsStarred() ? '#ffd700' : '#888'),
                        fontSize: '18px',
                        cursor: 'pointer',
                        padding: '3px 6px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                      }}
                      title="Star/Favorite Move"
                    >
                      <i className="fa-solid fa-star"></i>
                    </button>
                  </div>

                  {/* Row 3: Action Row */}
                  <div style={{ flex: 1, minHeight: '40px', padding: '5px' }}>
                    {toolMode === 'STAR' && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#ccc',
                        fontSize: '16px',
                        textAlign: 'center'
                      }}>
                        {getCurrentMoveIsStarred() ? (
                          <div 
                            onClick={() => {
                              if (currentMove !== '-1') {
                                dispatch({ type: 'TOGGLE_STAR_MOVE', payload: currentMove });
                              }
                            }}
                            style={{ 
                              color: '#ffd700', 
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 215, 0, 0.1)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            "{getCurrentMoveNotation()}" is Highlighted <i className="fa-solid fa-star"></i>
                          </div>
                        ) : (
                          <div 
                            onClick={() => {
                              if (currentMove !== '-1') {
                                dispatch({ type: 'TOGGLE_STAR_MOVE', payload: currentMove });
                              }
                            }}
                            style={{ 
                              color: '#888', 
                              fontStyle: 'italic',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(136, 136, 136, 0.1)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            "{getCurrentMoveNotation()}" - Click to Highlight <i className="fa-solid fa-star"></i>
                          </div>
                        )}
                      </div>
                    )}

                    {toolMode === 'SQUARE' && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '3px'
                      }}>
                        {Object.entries(constants.CHESSBOARD.COLOR_VARIANTS).map(([name, color]) => (
                          <button
                            key={name}
                            onClick={() => setSelectedColor(color)}
                            style={{
                              backgroundColor: color,
                              border: selectedColor === color ? '2px solid white' : '1px solid #666',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              transition: 'border 0.2s ease'
                            }}
                            title={name}
                          />
                        ))}
                        <button
                          onClick={() => {
                            // Clear board visual state immediately
                            if (boardRef?.current?.clearAllSquares) {
                              boardRef.current.clearAllSquares();
                            }
                            
                            // Clear all highlighted squares in Redux state
                            if (currentMove === '-1') {
                              dispatch({
                                type: 'UPDATE_START_POSITION_VISUALS',
                                payload: {
                                  highlightedSquares: {},
                                  arrows: state.startPosition?.arrows || []
                                }
                              });
                            } else if (currentMove && !currentMove.includes('|')) {
                              dispatch({
                                type: 'UPDATE_MOVE_PROPERTIES',
                                payload: {
                                  moveId: currentMove,
                                  properties: {
                                    highlightedSquares: {}
                                  }
                                }
                              });
                            }
                          }}
                          style={{
                            backgroundColor: '#d64545',
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                          title="Clear Squares"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    {toolMode === 'ARROW' && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '3px'
                      }}>
                        {arrowStartSquare && (
                          <span style={{ color: '#58b047', fontSize: '12px', marginRight: '4px' }}>
                            from {arrowStartSquare}
                          </span>
                        )}
                        {Object.entries(constants.CHESSBOARD.COLOR_VARIANTS).map(([name, color]) => (
                          <button
                            key={name}
                            onClick={() => setSelectedColor(color)}
                            style={{
                              backgroundColor: color,
                              border: selectedColor === color ? '2px solid white' : '1px solid #666',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              cursor: 'pointer',
                              transition: 'border 0.2s ease'
                            }}
                            title={name}
                          />
                        ))}
                        <button
                          onClick={() => {
                            // Clear board visual state immediately
                            if (boardRef?.current?.clearAllArrows) {
                              boardRef.current.clearAllArrows();
                            }
                            
                            // Clear all arrows and reset arrow start
                            setArrowStartSquare(null);
                            if (currentMove === '-1') {
                              dispatch({
                                type: 'UPDATE_START_POSITION_VISUALS',
                                payload: {
                                  arrows: [],
                                  highlightedSquares: state.startPosition?.highlightedSquares || {}
                                }
                              });
                            } else if (currentMove && !currentMove.includes('|')) {
                              dispatch({
                                type: 'UPDATE_MOVE_PROPERTIES',
                                payload: {
                                  moveId: currentMove,
                                  properties: {
                                    arrows: []
                                  }
                                }
                              });
                            }
                          }}
                          style={{
                            backgroundColor: '#d64545',
                            border: 'none',
                            borderRadius: '4px',
                            color: 'white',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: 'bold'
                          }}
                          title="Clear Arrows"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    {toolMode === 'TEXT' && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        height: '100%',
                        width: '100%',
                        maxWidth: '100%',
                        marginTop: '4px',
                        boxSizing: 'border-box'
                      }}>
                        <input
                          key={`annotation-2-${currentMove}-${isCommentMode}`}
                          type="text"
                          value={annotationContent}
                          onChange={(e) => {
                            const filteredValue = cleanProfanity(e.target.value);
                            setAnnotationContent(filteredValue);
                          }}
                          onBlur={() => {
                            // Save annotation content when user finishes editing
                            if (isCommentMode) {
                              // In comment mode, save directly to move properties (same as arrows)
                              // console.log(`[CommentBug] MobileMoves onBlur (second handler): Saving annotation content via dispatch:`, {
                              //   currentMove,
                              //   annotationContent,
                              //   isCommentMode
                              // });
                              
                              // Use same dispatch pattern as arrows to save content to move object
                              if (currentMove.startsWith('-1|')) {
                                // Start position variation - use specialized action (same as arrows)
                                dispatch({ 
                                  type: 'UPDATE_START_POSITION_VARIATION_MOVE_ANALYSIS', 
                                  payload: { 
                                    moveId: currentMove,
                                    properties: { content: annotationContent }
                                  }
                                });
                              } else {
                                // Regular move - use standard action (same as arrows)
                                dispatch({ 
                                  type: 'UPDATE_MOVE_PROPERTIES', 
                                  payload: { 
                                    moveId: currentMove,
                                    properties: { content: annotationContent }
                                  }
                                });
                              }
                            } else {
                              // Normal mode - save to game state
                              if (currentMove === '-1') {
                                dispatch({
                                  type: 'UPDATE_START_POSITION_CONTENT',
                                  payload: annotationContent
                                });
                              } else if (currentMove && !currentMove.includes('|')) {
                                const moveIndex = parseInt(currentMove);
                                dispatch({
                                  type: 'UPDATE_MOVE_CONTENT',
                                  payload: { moveId: currentMove, content: annotationContent }
                                });
                              } else if (currentMove && currentMove.includes('|')) {
                                // Handle variation content updates
                                const parts = currentMove.split('|');
                                
                                if (parts[0] === '-1') {
                                  // Start position variation: -1|varIdx:moveIdx
                                  const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                                  dispatch({
                                    type: 'UPDATE_START_POSITION_VARIATION_MOVE_CONTENT',
                                    payload: {
                                      variationIdx: varIdx,
                                      moveIdx: moveIdx,
                                      content: annotationContent
                                    }
                                  });
                                } else if (parts.length === 2) {
                                  // First-level variation: mainMoveIdx|varIdx:moveIdx
                                  const mainMoveIdx = parseInt(parts[0]);
                                  const [varIdx, moveIdx] = parts[1].split(':').map(Number);
                                  dispatch({
                                    type: 'UPDATE_MOVE_VARIATION_CONTENT',
                                    payload: {
                                      mainMoveIdx: mainMoveIdx,
                                      variationIdx: varIdx,
                                      moveIdx: moveIdx,
                                      content: annotationContent
                                    }
                                  });
                                } else {
                                  // Nested variation (2+ levels): use new action that handles nested paths correctly
                                  dispatch({
                                    type: 'UPDATE_NESTED_VARIATION_CONTENT',
                                    payload: {
                                      path: currentMove,
                                      content: annotationContent
                                    }
                                  });
                                }
                              }
                            }
                          }}
                          placeholder="Enter annotation..."
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            backgroundColor: '#1a1a1a',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            color: '#ccc',
                            padding: '4px 6px',
                            fontSize: '13px',
                            outline: 'none',
                            height: '28px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              );
            }
          })()
        ) : activeTab === 'settings' ? (
          <div style={{
            padding: '12px',
            backgroundColor: 'transparent',
            borderRadius: '0px',
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            flex: 1
          }}>
            <h3 style={{
              color: '#6bff6b',
              margin: '0 0 8px 0',
              fontSize: '14px'
            }}>
              Settings
            </h3>
            <div style={{
              color: '#aaffaa',
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              <div>
                <p style={{ margin: '0 0 4px 0' }}>Mutual settings for both</p>
                <p style={{ margin: '0' }}>AddGame and ViewGame</p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const MobileMoves = ({ 
  showStockfish = false,
  stockfishEvaluation = null,
  height = 120,
  comments = [],
  hideMainLineHeader = false,  // Add prop to hide Main Line Moves header in Record mode
  // Puzzle mode props for restricting navigation
  gameHasPuzzleTag = false,
  puzzleComplete = false,
  // Record mode autoplay integration
  toggleAutoPlayCallback = null,
  autoPlayState = null
}) => {
  const [activeTab, setActiveTab] = useState('moves');
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Use external autoplay state if provided (for Record mode integration)
  const effectiveIsPlaying = autoPlayState !== null ? autoPlayState : isPlaying;
  const [toolMode, setToolMode] = useState('NORMAL');
  const [selectedColor, setSelectedColor] = useState('#ff0000');
  const [popupContent, setPopupContent] = useState('');
  const { moves, currentMove, goToMove } = useGameLogic();
  const { state, dispatch } = useGameState();
  const moveRefs = useRef(new Map());
  const variationMoveRefs = useRef(new Map());
  
  // Parse current move to get index
  const currentMoveIndex = currentMove === '-1' ? -1 : parseInt(currentMove) || 0;
  const isInVariation = currentMove.includes('|');
  
  // Check if navigation should be disabled due to puzzle mode
  const isPuzzleNavigationDisabled = gameHasPuzzleTag && !puzzleComplete;
  
  // Auto-play functionality - plays through all main line moves
  useEffect(() => {
    // Skip basic autoplay if using external Record mode autoplay
    if (toggleAutoPlayCallback) {
      return;
    }
    
    let interval;
    if (isPlaying && currentMoveIndex < moves.length - 1) {
      interval = setInterval(() => {
        const nextIndex = currentMoveIndex + 1;
        if (nextIndex < moves.length) {
          goToMove(String(nextIndex));
        } else {
          // Reached the end, stop playing
          setIsPlaying(false);
        }
      }, 500); // 1 second interval between moves
    } else if (isPlaying && currentMoveIndex >= moves.length - 1) {
      // At the end, stop playing
      setIsPlaying(false);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, moves.length, goToMove]);
  
  // Handle play button click
  const handlePlayClick = () => {
    // Use Record mode callback if available
    if (toggleAutoPlayCallback) {
      console.log('[DEBUG] Using Record mode autoplay callback');
      toggleAutoPlayCallback();
      return;
    }
    
    if (currentMoveIndex >= moves.length - 1) {
      // At the end, restart from beginning
      goToMove('-1');
      setTimeout(() => setIsPlaying(true), 100);
    } else {
      // Toggle play state
      setIsPlaying(!isPlaying);
    }
  };

  // Function to scroll current move into view
  const scrollToCurrentMove = () => {
    const currentElement = moveRefs.current.get(currentMoveIndex);
    if (currentElement) {
      currentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  };

  // Scroll to current move within its variation container
  const scrollToCurrentVariationMove = () => {
    if (!currentMove || !currentMove.includes('|')) return;

    const variationKey = `variation-${currentMove}`;
    const moveElement = variationMoveRefs.current.get(variationKey);
    
    if (moveElement) {
      // The button is now a direct child of the scroll container
      // So we just scroll it into view directly
      moveElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  };

  // Watch for currentMove changes and scroll to keep it visible
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToCurrentMove();
      // Also scroll variation moves if we're in a variation
      if (currentMove && currentMove.includes('|')) {
        scrollToCurrentVariationMove();
      }
    }, 100); // Small delay to ensure DOM is updated
    
    return () => clearTimeout(timer);
  }, [currentMove]);
  
  // Render move button with comprehensive styling - Main MobileMoves version
  const renderMoveButton = (move, index, starredMoves = []) => {
    const isCurrentMove = index === currentMoveIndex;
    const moveNumber = Math.floor(index / 2) + 1;
    const isWhiteMove = index % 2 === 0;
    const moveHasVariations = hasVariations(move);
    
    const styling = getMoveButtonStyle(move, index, isCurrentMove, false, moveHasVariations, starredMoves, 'main');
    
    return (
      <div key={index} style={{ position: 'relative', display: 'inline-block' }}>
        <button
          ref={(el) => {
            if (el) {
              moveRefs.current.set(index, el);
            } else {
              moveRefs.current.delete(index);
            }
          }}
          onClick={() => goToMove(String(index))}
          style={{
            padding: '6px 10px',
            margin: '2px',
            backgroundColor: styling.backgroundColor,
            color: styling.color,
            border: styling.border || 'none',
            borderLeft: styling.borderLeft,
            borderBottom: styling.borderBottom,
            borderRadius: '4px',
            fontSize: '13px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            minWidth: '60px',
            transition: 'all 0.2s ease',
            position: 'relative'
          }}
        >
          {/* Gold star before title for starred moves */}
          {styling.starred && (
            <i 
              className="fa-solid fa-star" 
              style={{
                color: '#ffd700',
                fontSize: '12px',
                marginRight: '4px',
                verticalAlign: 'top'
              }}
            />
          )}
          {isWhiteMove && <span style={{ color: isCurrentMove ? 'black' : '#888', marginRight: '4px' }}>{moveNumber}.</span>}
          {move.notation}
        </button>
      </div>
    );
  };
  
  // Group moves into pairs for display
  const renderMovePairs = () => {
    const pairs = [];
    for (let i = 0; i < moves.length; i += 2) {
      pairs.push(
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {moves[i] && renderMoveButton(moves[i], i, state?.starredMoves)}
          {moves[i + 1] && renderMoveButton(moves[i + 1], i + 1, state?.starredMoves)}
        </div>
      );
    }
    return pairs;
  };
  
  return (
    <div style={{
      width: '100%',
      maxWidth: '100vw',
      height: `${Math.max(height, 110)}px`,
      backgroundColor: '',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      boxSizing: 'border-box',
      
    }}>
      {/* Navigation Controls - Now at top */}
      {moves.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          padding: '5px 4px',
          backgroundColor: '#0a0a0a',
          borderRadius: '10px'
        }}>
          {/* First Move Button - Context aware */}
          <button
            onClick={() => {
              setIsPlaying(false);
              if (isInVariation) {
                // Jump to parent move that spawned this variation
                const parentPath = getParentPath(currentMove);
                if (parentPath) {
                  goToMove(parentPath);
                }
              } else {
                // Main line - go to start position
                goToMove('-1');
              }
            }}
            disabled={(() => {
              if (isPuzzleNavigationDisabled) {
                return true; // Disabled in puzzle mode until complete
              } else if (isInVariation) {
                const parentPath = getParentPath(currentMove);
                return !parentPath;
              } else {
                return currentMove === '-1';
              }
            })()}
            style={{
              padding: '2px 6px',
              backgroundColor: 'transparent',
              color: (() => {
                if (isPuzzleNavigationDisabled) {
                  return '#333'; // Disabled color for puzzle mode
                } else if (isInVariation) {
                  const parentPath = getParentPath(currentMove);
                  return !parentPath ? '#333' : 'gold';
                } else {
                  return currentMove === '-1' ? '#333' : 'gold';
                }
              })(),
              border: 'none',
              fontSize: '18px',
              cursor: (() => {
                if (isPuzzleNavigationDisabled) {
                  return 'default'; // Default cursor for disabled puzzle mode
                } else if (isInVariation) {
                  const parentPath = getParentPath(currentMove);
                  return !parentPath ? 'default' : 'pointer';
                } else {
                  return currentMove === '-1' ? 'default' : 'pointer';
                }
              })(),
            }}
            title={isInVariation ? "Go to parent move" : "Go to start position"}
          >
            <i className="fa-solid fa-backward-fast"></i>
          </button>
          
          {/* Previous Move Button - One move back in current variation/line */}
          <button
            onClick={() => {
              setIsPlaying(false);
              const previousPath = getPreviousMovePath(currentMove, moves);
              if (previousPath) {
                goToMove(previousPath);
              }
            }}
            disabled={(() => {
              if (isPuzzleNavigationDisabled) {
                return true; // Disabled in puzzle mode until complete
              } else {
                const previousPath = getPreviousMovePath(currentMove, moves);
                return !previousPath;
              }
            })()}
            style={{
              padding: '2px 6px',
              backgroundColor: 'transparent',
              color: (() => {
                if (isPuzzleNavigationDisabled) {
                  return '#333'; // Disabled color for puzzle mode
                } else {
                  const previousPath = getPreviousMovePath(currentMove, moves);
                  return !previousPath ? '#333' : 'gold';
                }
              })(),
              border: 'none',
              fontSize: '18px',
              cursor: (() => {
                if (isPuzzleNavigationDisabled) {
                  return 'default'; // Default cursor for disabled puzzle mode
                } else {
                  const previousPath = getPreviousMovePath(currentMove, moves);
                  return !previousPath ? 'default' : 'pointer';
                }
              })()
            }}
            title="Previous move in current line"
          >
            <i className="fa-solid fa-angle-left"></i>
          </button>
          
          {/* Play/Pause Button in Center */}
          <button
            onClick={handlePlayClick}
            disabled={moves.length === 0}
            style={{
              padding: '2px 8px',
              backgroundColor: 'transparent',
              color: moves.length === 0 ? '#333' : (effectiveIsPlaying ? '#e91e63' : 'gold'),
              border: 'none',
              fontSize: '18px',
              cursor: moves.length === 0 ? 'default' : 'pointer'
            }}
            title={effectiveIsPlaying ? "Pause auto-play" : "Auto-play moves"}
          >
            <i className={effectiveIsPlaying ? "fa-solid fa-pause" : "fa-solid fa-play"}></i>
          </button>
          
          {/* Next Move Button - One move forward in current variation/line */}
          <button
            onClick={() => {
              setIsPlaying(false);
              const nextPath = getNextMovePath(currentMove, moves);
              if (nextPath) {
                goToMove(nextPath);
              }
            }}
            disabled={(() => {
              if (isPuzzleNavigationDisabled) {
                return true; // Disabled in puzzle mode until complete
              } else {
                const nextPath = getNextMovePath(currentMove, moves);
                return !nextPath;
              }
            })()}
            style={{
              padding: '2px 6px',
              backgroundColor: 'transparent',
              color: (() => {
                if (isPuzzleNavigationDisabled) {
                  return '#333'; // Disabled color for puzzle mode
                } else {
                  const nextPath = getNextMovePath(currentMove, moves);
                  return !nextPath ? '#333' : 'gold';
                }
              })(),
              border: 'none',
              fontSize: '18px',
              cursor: (() => {
                if (isPuzzleNavigationDisabled) {
                  return 'default'; // Default cursor for disabled puzzle mode
                } else {
                  const nextPath = getNextMovePath(currentMove, moves);
                  return !nextPath ? 'default' : 'pointer';
                }
              })()
            }}
            title="Next move in current line"
          >
            <i className="fa-solid fa-angle-right"></i>
          </button>
          
          {/* Last Move Button - Jump to end of current variation or main line */}
          <button
            onClick={() => {
              setIsPlaying(false);
              if (isInVariation) {
                const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
                if (endOfLine) {
                  goToMove(endOfLine);
                }
              } else {
                // Main line - go to last move
                goToMove(String(moves.length - 1));
              }
            }}
            disabled={(() => {
              if (isPuzzleNavigationDisabled) {
                return true; // Disabled in puzzle mode until complete
              } else if (isInVariation) {
                const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
                return !endOfLine;
              } else {
                return currentMoveIndex === moves.length - 1;
              }
            })()}
            style={{
              padding: '2px 6px',
              backgroundColor: 'transparent',
              color: (() => {
                if (isPuzzleNavigationDisabled) {
                  return '#333'; // Disabled color for puzzle mode
                } else if (isInVariation) {
                  const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
                  return !endOfLine ? '#333' : 'gold';
                } else {
                  return currentMoveIndex === moves.length - 1 ? '#333' : 'gold';
                }
              })(),
              border: 'none',
              fontSize: '18px',
              cursor: (() => {
                if (isPuzzleNavigationDisabled) {
                  return 'default'; // Default cursor for disabled puzzle mode
                } else if (isInVariation) {
                  const endOfLine = getEndOfCurrentLine(currentMove, moves, state);
                  return !endOfLine ? 'default' : 'pointer';
                } else {
                  return currentMoveIndex === moves.length - 1 ? 'default' : 'pointer';
                }
              })()
            }}
            title={isInVariation ? "Go to end of current variation" : "Go to end of game"}
          >
            <i className="fa-solid fa-forward-fast"></i>
          </button>
        </div>
      )}
      
      {/* Content Area */}
      <div style={{
        width: '100%',
        padding: '8px',
        paddingTop: '8px',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        boxSizing: 'border-box'
      }}>
        {activeTab === 'moves' ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {!hideMainLineHeader && (
              <div style={{ 
                fontSize: '16px', 
                color: '#1a1a1a', 
                fontWeight: 'bold', 
                marginBottom: '8px',
                backgroundColor: '#58b047',
                padding: '3px 6px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span>
                  Main Line Moves
                </span>
              </div>
            )}
            <div 
              className="mobile-moves-scrollbar"
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                gap: '8px',
                overflowX: 'auto',
                paddingBottom: '8px'
              }}>
              {/* Start Position Button */}
              <button
                ref={(el) => {
                  if (el) {
                    moveRefs.current.set(-1, el);
                  } else {
                    moveRefs.current.delete(-1);
                  }
                }}
                onClick={() => goToMove('-1')}
                style={{
                  padding: '6px 12px',
                  margin: '2px',
                  backgroundColor: currentMoveIndex === -1 ? '#58b047' : '#444',
                  color: currentMoveIndex === -1 ? 'black' : '#ccc',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  fontWeight: 'bold',
                  transition: 'all 0.2s ease'
                }}
              >
                START
              </button>
              
              {/* Moves */}
              {moves.length > 0 ? (
                renderMovePairs()
              ) : (
                <div style={{
                  color: '#666',
                  fontSize: '13px',
                  padding: '10px',
                  fontStyle: 'italic'
                }}>
                  No moves yet
                </div>
              )}
            </div>
            
            {/* Variation Row */}
            {renderVariationRow(getCurrentMoveVariations(currentMove, moves, state), goToMove, currentMove, state, variationMoveRefs)}
          </div>
        ) : activeTab === 'stockfish' ? (
          <EngineTabContent 
            stockfishEvaluation={stockfishEvaluation}
          />
        ) : activeTab === 'custom' ? (
          (() => {
            // Detect if we're in AddGame vs ViewGame based on URL path
            const isAddGame = window.location.pathname.includes('/mobile/add-game') || window.location.pathname.includes('/add-game');
            const isViewGame = window.location.pathname.includes('/mobile/games/') || window.location.pathname.includes('/games/');
            
            return (
              <div style={{
                padding: '12px',
                backgroundColor: 'transparent',
                boxSizing: 'border-box',
                borderRadius: '0px',
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                flex: 1
              }}>
                <h3 style={{
                  color: isAddGame ? '#ff6b6b' : isViewGame ? '#6b9eff' : '#fff',
                  margin: '0 0 8px 0',
                  fontSize: '14px'
                }}>
                  {isAddGame ? 'AddGame Mode' : isViewGame ? 'ViewGame Mode' : 'Unknown Mode'}
                </h3>
                <div style={{
                  color: isAddGame ? '#ffaaaa' : isViewGame ? '#aac4ff' : '#ccc',
                  fontSize: '12px',
                  lineHeight: '1.4'
                }}>
                  {isAddGame ? (
                    <div>
                      <p style={{ margin: '0 0 4px 0' }}>Create and edit games</p>
                      <p style={{ margin: '0' }}>Add annotations & arrows</p>
                    </div>
                  ) : isViewGame ? (
                    <div>
                      <p style={{ margin: '0 0 4px 0' }}>View and analyze games</p>
                      <p style={{ margin: '0' }}>Browse variations & comments</p>
                    </div>
                  ) : (
                    <div>
                      <p style={{ margin: '0' }}>Custom tab content</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        ) : activeTab === 'settings' ? (
          <div style={{
            padding: '12px',
            backgroundColor: 'transparent',
            borderRadius: '0px',
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            flex: 1
          }}>
            <h3 style={{
              color: '#6bff6b',
              margin: '0 0 8px 0',
              fontSize: '14px'
            }}>
              Settings
            </h3>
            <div style={{
              color: '#aaffaa',
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              <div>
                <p style={{ margin: '0 0 4px 0' }}>Mutual settings for both</p>
                <p style={{ margin: '0' }}>AddGame and ViewGame</p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      
      {/* Tab Buttons - Reordered: MOVES, CUSTOM, ENGINE, SETTINGS */}
      <div style={{
        display: 'flex',
        backgroundColor: '#1a1a1a',
        padding: '0px 4px',
        gap: '4px'
      }}>
        {/* First: MOVES */}
        <button
          onClick={() => setActiveTab('moves')}
          style={{
            flex: 1,
            padding: '8px',
            backgroundColor: activeTab === 'moves' ? '#58b047' : 'transparent',
            color: activeTab === 'moves' ? '#1a1a1a' : '#666',
            border: 'none',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: '12px'
          }}
        >
          MOVES
        </button>
        
        
        {/* Second: CUSTOM */}
        <button
          onClick={() => setActiveTab('custom')}
          style={{
            flex: 1,
            padding: '8px',
            backgroundColor: activeTab === 'custom' ? (window.location.pathname.includes('/games/') ? '#ff6600' : '#e91e63') : 'transparent',
            color: activeTab === 'custom' ? '#1a1a1a' : '#666',
            border: 'none',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: '12px'
          }}
        >
          CUSTOM
        </button>
        
        {/* Third: ENGINE */}
        {showStockfish && (
          <button
            onClick={() => setActiveTab('stockfish')}
            style={{
              flex: 1,
              padding: '8px',
              backgroundColor: activeTab === 'stockfish' ? '#ffd700' : 'transparent',
              color: activeTab === 'stockfish' ? '#1a1a1a' : '#666',
              border: 'none',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              borderRadius: '12px'
            }}
          >
            ENGINE
          </button>
        )}
        
        {/* Fourth: SETTINGS */}
        <button
          onClick={() => setActiveTab('settings')}
          style={{
            flex: 1,
            padding: '8px',
            backgroundColor: activeTab === 'settings' ? '#58b047' : 'transparent',
            color: activeTab === 'settings' ? '#000' : '#666',
            border: 'none',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            borderRadius: '12px'
          }}
        >
          SETTINGS
        </button>
      </div>
    </div>
  );
};

// Engine Tab Content Component - Shows Stockfish analysis directly
const EngineTabContent = ({ stockfishEvaluation }) => {
  const { state } = useGameState();
  const currentFEN = state.currentFEN;
  const currentMove = state.currentMove;
  
  // State for engine arrows setting
  const [engineArrows, setEngineArrows] = useState(() => {
    const saved = localStorage.getItem('engineArrows');
    // Don't use 'Hover' on mobile - default to 'Best' instead
    if (saved === 'Hover' || !saved) {
      return 'Best';
    }
    return saved;
  });

  // Clear engine arrows immediately when position changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
      detail: { arrows: [] }
    }));
  }, [currentMove, currentFEN]);

  // Clear engine arrows on component unmount
  useEffect(() => {
    return () => {
      window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
        detail: { arrows: [] }
      }));
    };
  }, []);
  
  // Use the unified Stockfish analysis hook
  const {
    analysis,
    isAnalyzing,
    source,
    error
  } = useUnifiedStockfishAnalysis(currentFEN, {
    forceLive: false,
    targetDepth: 15
  });

  const formatScoreDisplay = (cp) => {
    // Handle undefined/null values
    if (cp === undefined || cp === null) return 'N/A';
    
    // Handle mate scores (like "M5", "M-3")
    if (typeof cp === 'string' && cp.startsWith('M')) {
      const mateNumber = cp.substring(1);
      const mateValue = parseInt(mateNumber);
      
      if (!isNaN(mateValue)) {
        if (mateValue > 0) {
          return `M${mateValue}`; // User wins in X moves
        } else {
          return `M${mateValue}`; // User loses in X moves
        }
      }
      return cp; // Return as-is if can't parse
    }
    
    // Handle numeric scores
    const numericCp = typeof cp === 'number' ? cp : parseFloat(cp);
    if (!isNaN(numericCp)) {
      return numericCp > 0 ? `+${numericCp.toFixed(1)}` : numericCp.toFixed(1);
    }
    
    return 'N/A';
  };


  // Convert PV line to SAN notation for display
  const convertPVToSAN = (line) => {
    if (line?.pvSAN && line.pvSAN.length > 0) {
      return line.pvSAN.slice(0, 6).join(' ');
    }
    
    // If we have LAN moves, try to convert them to SAN
    if (line?.pv && line.pv.length > 0 && currentFEN) {
      try {
        const chess = new Chess(currentFEN);
        const sanMoves = [];
        
        for (let i = 0; i < Math.min(6, line.pv.length); i++) {
          const move = chess.move(line.pv[i]);
          if (move) {
            sanMoves.push(move.san);
          } else {
            break; // Stop if move is invalid
          }
        }
        
        return sanMoves.join(' ');
      } catch (error) {
        // Fallback to LAN notation if SAN conversion fails
        return line.pv.slice(0, 6).join(' ');
      }
    }
    
    return 'No line available';
  };

  // Handle move selection - dispatch event to board
  const handleMoveSelection = (moveData) => {
    if (moveData.bestMoveLAN || moveData.bestMoveSAN) {
      window.dispatchEvent(new CustomEvent('stockfish-move-selected', {
        detail: {
          move: moveData.bestMoveLAN || moveData.bestMoveSAN,
          san: moveData.bestMoveSAN,
          source: 'mobile-engine-tab'
        }
      }));
    }
  };

  // Dispatch arrow updates based on engine arrows setting and analysis
  useEffect(() => {
    // If no analysis or arrows disabled, clear arrows
    if (!analysis?.pvs?.length || engineArrows === 'None') {
      window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
        detail: { arrows: [] }
      }));
      return;
    }

    // Ensure analysis is for the current position to prevent stale arrows
    if (analysis.fen && analysis.fen !== currentFEN) {
      window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
        detail: { arrows: [] }
      }));
      return;
    }

    // Determine how many arrows to show
    const arrowCount = engineArrows === 'Best' ? 1 : engineArrows === 'All' ? 3 : 0;
    
    if (arrowCount === 0) {
      window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
        detail: { arrows: [] }
      }));
      return;
    }

    // Build arrows array
    const colors = ['#ffd700', '#59b047', '#3066be']; // Gold, Green, Blue
    const arrows = [];
    const topMoves = analysis.pvs.slice(0, arrowCount);
    
    topMoves.forEach((line, index) => {
      if (line.bestMoveLAN && line.bestMoveLAN.length >= 4) {
        const from = line.bestMoveLAN.substring(0, 2);
        const to = line.bestMoveLAN.substring(2, 4);
        arrows.push([from, to, colors[index]]);
      }
    });

    // Dispatch arrows to board
    window.dispatchEvent(new CustomEvent('stockfish-arrows-update', {
      detail: { arrows }
    }));
  }, [engineArrows, analysis, currentFEN, currentMove]);

  // Show loading state
  if (isAnalyzing || !currentFEN) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        color: '#666',
        fontSize: '12px',
        height: '100%',
        width: '100%',
        backgroundColor: 'transparent',
        flex: 1
      }}>
        <span style={{ backgroundColor: '#ffd700', color: '#1a1a1a', padding: '4px 6px', borderRadius: '6px', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="fas fa-robot fa-bounce" style={{ fontSize: '16px' }}></i>
          Calculating...
        </span>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        color: '#666',
        fontSize: '12px',
        height: '100%',
        width: '100%',
        backgroundColor: 'transparent',
        flex: 1
      }}>
        <i className="fas fa-exclamation-triangle" style={{ fontSize: '16px' }}></i>
        <div>Engine unavailable</div>
      </div>
    );
  }

  // Show no analysis available
  if (!analysis || !analysis.pvs || analysis.pvs.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
        color: '#666',
        fontSize: '12px',
        height: '100%',
        width: '100%',
        backgroundColor: 'transparent',
        flex: 1
      }}>
        <span style={{ backgroundColor: '#ffd700', color: '#1a1a1a', padding: '4px 6px', borderRadius: '6px', fontSize: '16px', fontWeight: 'bold', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <i className="fas fa-robot fa-bounce" style={{ fontSize: '16px' }}></i>
          Waking up Stockfish...
        </span>
      </div>
    );
  }

  const colors = ['#ffd700', '#59b047', '#3066be'];
  const labels = ['Best:', '2nd:', '3rd:'];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      flex: 1,
      height: '100%',
      width: '100%',
      maxWidth: 'calc(100vw - 60px)',
      backgroundColor: 'transparent'
    }}>
      {/* Top 3 Moves */}
      <div 
        data-scrollable="true"
        style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0px', backgroundColor: '#ffd700', borderRadius: '6px',}}>
                <div style={{ 
                  fontSize: '16px', 
                  color: '#1a1a1a', 
                  fontWeight: 'bold',
                  
                  padding: '2px',
                  paddingLeft: '6px',
                  
                  display: 'inline-block'
                }}>Stockfish</div>
                <select 
                  style={{
                    backgroundColor: 'transparent',
                    color: 'black',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    padding: '2px 4px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    maxWidth: '120px',
                    overflow: 'hidden',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'><path fill='%23000000' d='m2 4-2-2h4z'/></svg>")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 6px center',
                    backgroundSize: '8px',
                    paddingRight: '10px',
                    marginRight: '5px'
                  }}
                  value={engineArrows}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    // Update local state
                    setEngineArrows(newValue);
                    // Save to localStorage
                    localStorage.setItem('engineArrows', newValue);
                    // Dispatch event for other components if needed
                    window.dispatchEvent(new CustomEvent('update-engine-arrows', {
                      detail: { value: newValue }
                    }));
                  }}
                >
                  <option value="None">No Arrows</option>
                  <option value="Best">Top Arrow</option>
                  <option value="All">All Arrows</option>
                </select>
       </div>
        {/* Top 3 moves in a row */}
        <div style={{
          display: 'flex',
          gap: '5px',
          width: '100%'
        }}>
          {analysis.pvs.slice(0, 3).map((line, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                backgroundColor: colors[index],
                borderRadius: '3px',
                padding: '4px 4px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: 'black',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'transform 0.1s ease'
              }}
            onClick={() => handleMoveSelection(line)}
            onMouseDown={(e) => {
              e.target.style.transform = 'scale(0.98)';
            }}
            onMouseUp={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              width: '100%',
              height: '100%',
              justifyContent: 'space-between'
            }}>
              
              {/* Top row - Move notation (large and centered) */}
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold',
                textAlign: 'center',
                lineHeight: '1',
                paddingTop: '2px',
                paddingBottom: '3px'
              }}>
                {line.bestMoveSAN}
              </div>
              
              {/* Bottom row - Label left, Score right */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'end',
                paddingBottom: '1px'
              }}>
                <div style={{ 
                  fontSize: '12px', 
                  opacity: 0.8,
                  lineHeight: '1',
                  background: 'black',
                  color: colors[index],
                  padding: '3px 6px',
                  borderRadius: '5px'
                }}>
                  {labels[index].replace(':', '')}
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold',
                  lineHeight: '1',
                  
                  borderRadius: '5px',
                  // border: `2px solid ${colors[index]}`,
                  // backgroundColor: 'black',
                  // color: colors[index]
                }}>
                  {formatScoreDisplay(line.normalizedCP)}
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>

        {/* Best line preview using component */}
        <BestLineComponent 
          analysis={analysis} 
          currentFEN={currentFEN} 
        />
        
      </div>
    </div>
  );
};

export default MobileMoves;